// Hush PWA entry point: rendering, navigation and event handling.
import { initPull } from './pull-input';
import { BACKGROUND, shouldBackgroundRefresh } from './pull';
import { acceptSheetFor, acceptStart, asideStartsOpen, grossFor, sharesText, vSplitSheet, type AcceptState } from './views/splits';
import { vMonthSheet } from './views/month';
import { MONTH_STARTED } from '../../shared/months';
import { tickedLines, vSettle, vSplitsRow } from './views/settle';
import { newFlow, vCardFlow } from './views/card-flow';
import { nothingSetUp, vBankSheet, vCardSheet, vCardsBanks } from './views/cards-banks';
import { afterAlerts, afterCompare, afterGate, afterGmail, type FlowStep, type GateAnswer } from '../../shared/card-flow';
import { settleVisible, splitsEnabled } from '../../shared/partner';
import { LINK_SPLIT_GONE, readPushLink, sheetFor, type PushLink } from './push-links';
import { confirmThenSplit, resendAfterEdit, validPercent, type SplitAfterEdit } from './new-split';
import { pushMenuStatus, vNotifications, type PushUi } from './views/notifications';
import { currentSubscription, deviceFacts, storedDeviceId, storeDeviceId, subscribeDevice, unsubscribeDevice } from './push-client';
import type { PushKind } from '../../shared/push';
import { parseRowMode, readRowMode, tickLeft, writeRowMode, type RowMode } from './views/catrow';
import { bulkFiledText, DONE_PERCENT_HINT, SPLIT_DEFAULT_PERCENT, SPLIT_NO_LINK, SPLIT_NOT_SENT, SPLIT_PERCENT_HINT, SPLIT_SENT, validSplitPercent } from '../../shared/splits';
import { TIP_BELOW_CHARGE, tipFor, tipFromTotal } from '../../shared/tips';
import { tipLine, tipOpener, tipProminent, type NewTip } from './views/tips';
import { AMOUNT_NEEDED, AMOUNT_SPLIT_ALONE, CHOOSE_FUND, openSentSplit, choicesOf, fundMissing, rowAmountPatch, rowCategory, rowCharge, rowNote, rowSplitNote, startSplit, type NewChoices, type RowChoices } from './views/newcard';
import { cleanNote, splitNoteOf } from '../../shared/notes';
import { noteChips, seesShown } from './views/notes';
import { fundSwitched, newSheetState, startTip, tipSwitched, vNewSheet, type NewSheetRow } from './views/newsheet';
import { fadeMs, isBusy, runBusy, type BusyButton, type BusyDeps } from './busy';
import { sheetFundBlock } from './views/savings';
import { adoptStoredToken, api, ApiError, errorData, handleUnauthorized, incomeFields, type IncomeFields, type ItemFields } from './api';
import { vPasteSheet } from './views/paste';
import { vRecoveryCode, type RecoveryContext } from './views/recovery';
import type { FeaturesDto, LearnedDto, OverviewDto, PastePreviewResponse, PushViewDto, SettleViewDto } from '../../shared/api';
import { esc, isoDay, money, parseCents, setCurrency } from './format';
import { catName, store, ui, type Tab } from './state';
import type { GroupDto, IncomeLineDto, ItemDto, RenewalAnswer, ReviewItemDto, TransactionDto } from '../../shared/api';
import { vActivity } from './views/activity';
import { refreshBudgetNumbers, vBudget } from './views/budget';
import { vGroupSheet } from './views/groups';
import { vDetailsSheet, vIncomeSheet } from './views/income';
import {
  AUTO_LOG_NOTE,
  AUTO_LOG_OFF_NOTE,
  SPREAD_AUTO_LOG_NOTE,
  yearlySummaryText,
  itemPicker,
  removedText,
  vCandidateCategory,
  vCategory,
  vDoneSheet,
  vItemSheet,
  vRenewedSheet,
} from './views/items';
import { vDeleteAccountSheet, vSettings, vSettingsPage, vUseInviteSheet, type SettingsPage } from './views/settings';
import { vLearned } from './views/learned';
import { vInsights } from './views/insights';
import { vOverview } from './views/overview';
import { clampDate, earlierLabel, learnLine, loggedText, prefillCents, refillAmount, vSheet } from './views/sheet';
import { vWelcome, type InviteInfo, type WelcomeMode } from './views/welcome';
import { draftFrom, drawerPatch, isDirty, SAME_AMOUNT_HINT, SET_AMOUNT_FIRST, switchMode, vCategoryDrawer, type Draft } from './views/drawer';
import { NOT_SAVED_TEXT, SAVED_TEXT, wireInline } from './inline';
import { onCentsBeforeInput, onCentsInput, setCents } from './cents-input';
import { yearlySchedule } from '../../shared/yearly';
import { CROSS_KIND } from '../../shared/groups';
import { initDrag } from './drag';
import { ScrollMemory } from './scroll';
import { navigateTo, navOf, type NavTarget } from './nav';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

const NAV: { id: Tab; label: string; ic: string }[] = [
  { id: 'overview', label: 'Overview', ic: '<path d="M4 13h6V4H4zM14 20h6V10h-6zM4 20h6v-3H4zM14 7h6V4h-6z"/>' },
  { id: 'activity', label: 'Activity', ic: '<path d="M5 7h14M5 12h14M5 17h9"/>' },
  { id: 'insights', label: 'Insights', ic: '<path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z"/>' },
  {
    id: 'budget',
    label: 'Budget',
    ic: '<path d="M5 8h9M18 8h1M5 16h1M10 16h9"/><circle cx="16" cy="8" r="2"/><circle cx="8" cy="16" r="2"/>',
  },
];

// ---- Rendering --------------------------------------------------------------

function showChrome(on: boolean) {
  $('nav').hidden = !on;
  (document.querySelector('.fab') as HTMLElement).hidden = !on;
}

let welcomeMode: WelcomeMode = 'start';
/** An opened emailed invite: its token (memory only, never stored), who sent it, an email to prefill. */
let inviteToken: string | null = null;
let inviteInfo: InviteInfo | null = null;
let welcomeEmail = '';
/** While a recovery code is on screen, nothing else renders over it. Holds what to do on Continue. */
let recoveryNext: (() => void) | null = null;

function showRecoveryCode(code: string, context: RecoveryContext, next: () => void) {
  recoveryNext = next;
  showChrome(false);
  $('app').innerHTML = vRecoveryCode(code, context);
  window.scrollTo(0, 0);
}
/** Public deployment features, for the welcome screen (before sign-in). */
let publicFeatures: FeaturesDto | null = null;

function renderWelcome() {
  showChrome(false);
  $('app').innerHTML = vWelcome(welcomeMode, publicFeatures?.emailSignIn ?? false, inviteInfo, welcomeEmail);
}

/** Sign out locally and show the welcome screen. */
function signedOut() {
  store.data = null;
  settleData = null;
  go({ to: 'start' });
}

function render() {
  if (recoveryNext) return; // the "save this code" screen stays until Continue
  const d = store.data;
  if (!d) return renderWelcome();
  // The add-a-card flow is full screen: its own header, no tab bar, no +.
  if (ui.cardFlow) {
    showChrome(false);
    $('app').innerHTML = vCardFlow(d, ui.cardFlow);
    return;
  }
  showChrome(true);
  const views = {
    overview: () => vOverview(d, { dueOpen: dueOpen(), picks: ui.picks, newSplits: ui.newSplits, newTips: ui.newTips, newFunds: ui.newFunds, newItems: ui.newItems, newFundOn: ui.newFundOn, newNotes: ui.newNotes, newAmounts: ui.newAmounts, splits: settleData, rowMode }),
    activity: () => vActivity(d, activitySearch),
    insights: () => vInsights(d),
    budget: () => vBudget(d),
    settings: () => settingsScreen(d),
  };
  // Which screen shows is nav.ts's business; redrawing keeps the page where it is.
  const y = window.scrollY;
  $('app').innerHTML = ui.settle ? vSettle(settleData, ui.settleOff, ui.settleHistory) : ui.categoryId ? vCategory(d, ui.categoryId) : views[ui.tab]();
  renderNav();
  wireInlineFields();
  sizeBars($('app'));
  if (ui.learned) filterLearned($('app'), ui.learned.search);
  if (ui.tab === 'activity' && !ui.categoryId && !ui.settle) filterActivity($('app'), activitySearch);
  window.scrollTo(0, y);
}

// ---- Settings: the menu and its screens -----------------------------------------------

function settingsScreen(d: OverviewDto): string {
  const page = ui.settingsPage;
  // The Notifications row and page need this device's facts and the server's view.
  if (!pushUi && !pushLoading && !d.isSample) void loadPush();
  if (!page) return vSettings(d, pushMenuStatus(pushUi));
  if (page === 'notifications') return vNotifications(d, pushUi);
  if (page === 'learned') return vLearned(d, learnedData, ui.learned?.search ?? '');
  if (page === 'cards') return vCardsBanks(d);
  return vSettingsPage(d, page, ui.partner);
}

function openSettingsPage(page: SettingsPage) {
  if (page === 'learned') return actions['learned-open']({ dataset: {} } as unknown as HTMLElement);
  // Nothing set up yet: Cards and banks is the add-a-card flow itself.
  if (page === 'cards' && store.data && nothingSetUp(store.data) && store.data.features.inboundEmail && !store.data.isSample) return openFlow();
  // Opened from Overview (Finish setup): Back from the menu returns to that tab.
  go({ to: 'settings', page });
}

/** A sheet with ready-made HTML (card and bank details). */
function openRawSheet(html: string) {
  if (!html) return;
  ui.sheet = { id: null, cat: null, armed: false };
  $('sheet').innerHTML = html;
  $('scrim').classList.add('on');
  $('sheet').classList.add('on');
}

// ---- The add-a-card flow (full screen) ------------------------------------------------

/** One timer while the flow is open: it waits on the example, Gmail's code and the first alert. */
let flowTimer: ReturnType<typeof setInterval> | undefined;
let flowTicks = 0;

function openFlow() {
  const d = store.data;
  if (!d) return;
  // Every card starts fresh at the gate; only an example forwarded from now on counts.
  ui.cardFlow = { ...newFlow(new Date()), knownAtStart: d.cardsBanks.gmail.map((g) => g.address) };
  clearInterval(flowTimer);
  flowTicks = 0;
  flowTimer = setInterval(() => void flowTick(), 4000);
  // Under the flow: Settings, Cards and banks (where it closes to).
  go({ to: 'settings', page: 'cards' });
}

function closeFlow() {
  clearInterval(flowTimer);
  flowTimer = undefined;
  ui.cardFlow = null;
  // Back to Cards and banks.
  go({ to: 'settings', page: 'cards' });
  void refresh({ silent: true });
}

function renderFlow() {
  render();
  window.scrollTo(0, 0);
}

/** The next screen (the current one goes on the trail, for Back and progress). */
function flowGo(step: FlowStep) {
  const f = ui.cardFlow;
  if (!f || f.step === step) return;
  f.trail.push(f.step);
  f.step = step;
  // The alert steps show once per flow (afterCompare() skips the card-alerts step after this one).
  if (step === 'alerts-on') f.alertsShown = true;
  renderFlow();
  if (step === 'compare' && !f.compare) void loadCompare();
}

async function loadCompare() {
  const f = ui.cardFlow;
  if (!f?.example) return;
  try {
    f.compare = await api.flowCompare(f.example.id, f.gmail);
  } catch (e) {
    return toast(errorText(e));
  }
  if (ui.cardFlow === f && f.step === 'compare') render();
}

async function flowTick() {
  const f = ui.cardFlow;
  if (!f || document.visibilityState !== 'visible') return;
  flowTicks++;
  if (f.step === 'example' && !f.example) {
    try {
      const r = await api.flowExample(f.startedAt);
      if (ui.cardFlow !== f) return;
      if (r.example || r.notAlert !== f.notAlert) {
        f.example = r.example;
        f.notAlert = r.notAlert;
        render();
      }
    } catch {
      /* the next tick tries again */
    }
    return;
  }
  // Gmail's confirmation email (verify).
  if (f.step === 'verify') await refresh({ silent: true });
}

/** The first time, with nothing set up, the flow opens by itself for the first card (once per device). */
const FLOW_AUTO_KEY = 'hush-cardflow-auto';
let flowAutoChecked = false;
function maybeAutoOpenFlow() {
  const d = store.data;
  if (flowAutoChecked || !d) return;
  flowAutoChecked = true;
  if (!d.features.inboundEmail || d.isSample || !nothingSetUp(d) || d.cardsBanks.anyWorking || recoveryNext) return;
  try {
    if (localStorage.getItem(FLOW_AUTO_KEY)) return;
    localStorage.setItem(FLOW_AUTO_KEY, '1');
  } catch {
    return; // not remembered in a private window: do not open it on every visit
  }
  openFlow();
}

// ---- The Splits screen (over any tab) ------------------------------------------------

/** The Splits screen's data, also the Overview Splits row's net (kept between visits, reloaded with the month). */
let settleData: SettleViewDto | null = null;

/**
 * The Overview Splits row: reload the Splits screen's own data (GET /api/settle, read only) after
 * the month, then swap only the row. Its height is reserved, so nothing moves. A failed load keeps
 * the last net (or the quiet placeholder).
 */
async function loadSplitsRow() {
  const d = store.data;
  if (!d || d.isSample || !settleVisible(d.partner)) {
    settleData = null;
    return;
  }
  try {
    settleData = (await api.settle()).settle;
  } catch {
    return;
  }
  if (ui.settle) return render();
  const row = document.querySelector('[data-splits]');
  if (row && store.data === d) row.outerHTML = vSplitsRow(d, settleData);
}

async function loadSettle() {
  try {
    settleData = (await api.settle()).settle;
  } catch (e) {
    // Gone (unlinked and nothing left, say): back to where it was opened from.
    if (e instanceof ApiError && e.status === 404) return closeSettle();
    toast(errorText(e));
  }
  if (ui.settle) render();
}

function closeSettle() {
  if (ui.settle) go({ to: 'back' });
}

/** A settle up action, then the screen (and the overview: its card) again. */
/** A change on the Splits screen behind its button: loading state, the error under it on failure. */
async function settleChange(b: HTMLElement, work: () => Promise<{ settle: SettleViewDto }>, success: string) {
  await busyMutate(
    b,
    async () => {
      settleData = (await work()).settle;
    },
    { success },
  );
}

// ---- What Hush has learned (over Settings) -------------------------------------------

let learnedData: LearnedDto | null = null;

/** Activity's search box (merchant, bank text or note); a tab-bar tap clears it. */
let activitySearch = '';

/** Hide Activity rows the search does not match (in place, so typing keeps focus), and empty days. */
function filterActivity(root: ParentNode, search: string) {
  const q = search.trim().toLowerCase();
  // Purchases match on their text; share lines and Review items have none, so a search hides them.
  for (const row of root.querySelectorAll<HTMLElement>('.txg > .tx')) row.hidden = !!q && !row.dataset.q?.includes(q);
  for (const group of root.querySelectorAll<HTMLElement>('.txg')) {
    const any = [...group.children].some((c) => !(c as HTMLElement).hidden);
    group.hidden = !any;
    const day = group.previousElementSibling as HTMLElement | null;
    if (day?.classList.contains('day')) day.hidden = !any;
  }
  const none = root.querySelector<HTMLElement>('[data-search-none]');
  if (none) none.hidden = !q || !!root.querySelector('.tx[data-q]:not([hidden])');
}

/** Hide the rows the search does not match (in place, so typing keeps focus). */
function filterLearned(root: ParentNode, search: string) {
  const q = search.trim().toLowerCase();
  root.querySelectorAll<HTMLElement>('[data-learn-row]').forEach((row) => (row.hidden = !!q && !row.dataset.learnRow!.includes(q)));
}

async function loadLearned() {
  try {
    learnedData = await api.learned();
  } catch (e) {
    toast(errorText(e));
  }
  if (ui.learned) render();
}

/** A change on the learned screen, then the list again (the overview too: filing may differ). */
async function learnedChange(work: () => Promise<unknown>, success: string) {
  if (await mutate(work, success)) await loadLearned();
}

/**
 * Bar widths and pace ticks come as data-w / data-l (percent) and are set here through the DOM:
 * the Content-Security-Policy allows no inline style attributes.
 */
function sizeBars(root: ParentNode) {
  root.querySelectorAll<HTMLElement>('[data-w]').forEach((el) => (el.style.width = `${Number(el.dataset.w) || 0}%`));
  root.querySelectorAll<HTMLElement>('[data-l]').forEach((el) => (el.style.left = tickLeft(Number(el.dataset.l) || 0)));
}

// ---- Navigation and scroll positions ---------------------------------------------

const scrolls = new ScrollMemory();

/**
 * Change screen, the only way (nav.ts): remember where this one was, switch, draw, then scroll to
 * where the new one was left (a tab, the screen under a category, the Settings menu), or to the
 * top when it opens fresh. `draw: false` only sets the screen (a load follows).
 */
function go(target: NavTarget, opts: { draw?: boolean } = {}) {
  const next = navigateTo(scrolls, navOf(ui), target, window.scrollY);
  Object.assign(ui, next.state);
  if (opts.draw === false) return;
  render();
  window.scrollTo(0, next.y);
}

// ---- Settings, Notifications (web push) -------------------------------------------------

let pushUi: PushUi | null = null;
let pushLoading = false;

/** The server's view and this device's facts. "On" needs both a live subscription here and its row there. */
async function loadPush(): Promise<void> {
  pushLoading = true;
  try {
    const [server, sub] = await Promise.all([api.pushView(), currentSubscription().catch(() => null)]);
    const deviceId = sub ? storedDeviceId() : null;
    pushUi = { server, device: deviceFacts(), deviceId, busy: false };
  } catch {
    pushUi = null;
  } finally {
    pushLoading = false;
  }
  if (ui.tab === 'settings') render();
}

/** One push action: busy while it runs, the server's new view after, a toast on failure. */
async function pushAction(work: () => Promise<PushViewDto | void>, done?: string) {
  if (!pushUi) return;
  pushUi.busy = true;
  render();
  try {
    const server = await work();
    if (server && pushUi) pushUi.server = server;
    if (done) toast(done);
  } catch (e) {
    toast(errorText(e));
  }
  if (pushUi) {
    pushUi.busy = false;
    pushUi.device = deviceFacts();
  }
  render();
}

// ---- Still due on Overview: open or folded, remembered on this device ------------------

// ---- Overview: Planned / Spent / Remaining, remembered on this device -------------------

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null; // storage blocked: the choice lasts until the page closes
  }
};
let rowMode: RowMode = readRowMode(storage());

const DUE_KEY = 'hush-due-open';
function dueOpen(): boolean {
  try {
    return localStorage.getItem(DUE_KEY) === '1';
  } catch {
    return false;
  }
}

// ---- Budget tab: inline amounts ------------------------------------------------

/** Save an inline amount (planned amount, room for other spending, or an item), then update the numbers in place. */
async function commitInline(input: HTMLInputElement, raw: string): Promise<'unchanged' | 'saved' | { error: string }> {
  const d = store.data!;
  const cents = raw.trim() === '' ? 0 : parseCents(raw);
  if (!(cents >= 0)) return { error: 'Enter an amount, or 0.' };
  const id = input.dataset.id!;
  const kind = input.dataset.inline;
  if (kind === 'item') {
    const item = d.items.find((i) => i.id === id);
    if (!item || item.amountCents === cents) return 'unchanged';
    await api.updateItem(id, { amountCents: cents });
  } else {
    const k = d.categories.find((x) => x.id === id);
    if (!k) return 'unchanged';
    if (kind === 'extra' ? k.extraCents === cents : k.budgetCents === cents) return 'unchanged';
    await api.updateCategory(id, kind === 'extra' ? { extraCents: cents } : { budgetCents: cents });
  }
  input.value = String(cents / 100);
  // Totals and insights depend on amounts. Reload the data quietly and update the numbers in place:
  // re-rendering would rebuild the fields under the user's finger.
  try {
    store.data = await api.overview(ui.month ?? undefined);
    refreshBudgetNumbers(store.data);
    renderNav();
  } catch {
    /* saved; the numbers catch up on the next refresh */
  }
  return 'saved';
}

function wireInlineFields() {
  document.querySelectorAll<HTMLInputElement>('#app [data-inline]').forEach((input) => {
    const cell = input.closest<HTMLElement>('.amtcell')!;
    const st = cell.querySelector<HTMLElement>('.st')!;
    wireInline(
      input,
      (raw) => commitInline(input, raw),
      (state, message) => {
        for (const c of ['saving', 'saved', 'error'] as const) cell.classList.toggle(c, state === c);
        st.textContent = state === 'saved' ? SAVED_TEXT : state === 'error' ? NOT_SAVED_TEXT : '';
        input.setAttribute('aria-invalid', String(state === 'error'));
        if (state === 'error' && message) toast(message);
      },
    );
  });
}

// ---- Category drawer ----------------------------------------------------------

const categoryById = (id: string) => store.data?.categories.find((k) => k.id === id);

function openDrawer(id: string, draft?: Draft) {
  const k = categoryById(id);
  if (!k) return;
  ui.sheet = { id: null, cat: id, armed: false };
  ui.drawer = { id, draft: draft ?? draftFrom(k), confirm: false };
  renderDrawer();
  $('scrim').classList.add('on');
  $('sheet').classList.add('on', 'full');
}

function renderDrawer(error?: string) {
  const dr = ui.drawer;
  const k = dr && categoryById(dr.id);
  if (!dr || !k) return closeDrawer();
  $('sheet').innerHTML = vCategoryDrawer(store.data!, k, dr.draft, { confirm: dr.confirm, error });
}

/** Typed values into the draft, before the drawer is drawn again. */
function readDraft() {
  const dr = ui.drawer;
  if (!dr) return;
  const v = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value;
  dr.draft = {
    ...dr.draft,
    name: v('dr-name') ?? dr.draft.name,
    amount: v('dr-amt') ?? dr.draft.amount,
    room: v('dr-room') ?? dr.draft.room,
    groupId: v('dr-group') ?? dr.draft.groupId,
    note: v('dr-note') ?? dr.draft.note,
    dueDay: v('dr-due') ?? dr.draft.dueDay,
  };
  const check = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.checked;
  dr.draft.sameAmount = check('dr-same') ?? dr.draft.sameAmount;
  dr.draft.autoLog = check('dr-auto') ?? dr.draft.autoLog;
}

/** Close the drawer without asking (saved, discarded or deleted). */
function closeDrawer() {
  ui.drawer = null;
  ui.drawerReturn = null;
  closeSheet();
}

/** Cancel, the scrim, Escape or the purchases link: with unsaved edits, ask "Discard changes?" first. */
function requestClose(after?: () => void) {
  const dr = ui.drawer;
  if (dr) {
    readDraft();
    const k = categoryById(dr.id);
    if (k && isDirty(k, dr.draft)) {
      dr.confirm = true;
      dr.after = after;
      renderDrawer();
      return;
    }
    closeDrawer();
  } else closeSheet();
  after?.();
}

/** The item editor from the drawer: closing it (saved or not) comes back to the drawer and its draft. */
function itemFromDrawer(item: ItemDto | null) {
  const dr = ui.drawer!;
  readDraft();
  ui.drawerReturn = { ...dr, confirm: false };
  ui.drawer = null;
  openItemSheet(item, dr.id);
}

function renderNav() {
  const n = store.data?.insights.length ?? 0;
  $('nav').innerHTML = NAV.map(
    (t) =>
      `<button data-a="tab" data-tab="${t.id}" ${ui.tab === t.id ? 'aria-current="page"' : ''}><svg viewBox="0 0 24 24" aria-hidden="true">${t.ic}</svg>${t.label}${t.id === 'insights' && n ? `<span class="badge">${n}</span>` : ''}</button>`,
  ).join('');
}

/** When the data last came from the server (for the foreground and background refreshes). */
let lastLoaded = 0;
/** A load is in flight (the background refresh waits for it). */
let loading = false;

/**
 * Reload the month and redraw in place (render() keeps the scroll; the drawer keeps its draft).
 * `silent`: the background refresh, which shows no error.
 */
async function refresh(opts: { silent?: boolean } = {}) {
  loading = true;
  // A saved note is a new suggestion: chips are fetched again next time.
  noteCache.clear();
  try {
    store.data = await api.overview(ui.month ?? undefined);
    lastLoaded = Date.now();
    setCurrency(store.data.household.currency);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) store.data = null;
    else if (opts.silent) return;
    else toast(errorText(e));
  } finally {
    loading = false;
  }
  render();
  maybeAutoOpenFlow();
  void loadSplitsRow();
  // The drawer follows the data (an item added from it, say), keeping what was typed.
  if (ui.drawer) {
    readDraft();
    renderDrawer();
  }
}

const errorText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Try again.');

/** Run a mutation, then reload the month. Shows the error as a toast if it fails. */
async function mutate(work: () => Promise<unknown>, success?: string | (() => string), undo?: () => Promise<unknown>) {
  try {
    await work();
  } catch (e) {
    toast(errorText(e));
    return false;
  }
  await refresh();
  if (success) toast(typeof success === 'function' ? success() : success, undo ? () => void mutate(undo) : undefined);
  return true;
}

// ---- Toast and sheet -------------------------------------------------------------

/** How long a toast (and its Undo) stays: long enough to reach Undo after a one-tap delete. */
const TOAST_MS = 6000;

function toast(msg: string, undo?: () => void) {
  const t = $('toast');
  t.innerHTML = `<span>${esc(msg)}</span>${undo ? '<button data-a="undo">Undo</button>' : ''}`;
  ui.undo = undo ?? null;
  t.classList.add('on');
  clearTimeout(ui.toastTimer);
  ui.toastTimer = setTimeout(() => t.classList.remove('on'), TOAST_MS);
}

/** The add/edit sheet. `preset` picks a category (and an item) for a new purchase, still changeable. */
function openSheet(id: string | null, fromReview?: ReviewItemDto, preset: { cat?: string; item?: string } = {}) {
  const d = store.data!;
  const t = id ? (d.transactions.find((x) => x.id === id) ?? null) : null;
  ui.sheet = { id: t?.id ?? null, cat: t?.categoryId ?? preset.cat ?? null, item: t?.itemId ?? preset.item ?? null, armed: false, reviewId: fromReview?.id };
  $('sheet').innerHTML = vSheet(d, t, ui.sheet.cat, fromReview ? { amountCents: fromReview.amountCents, date: fromReview.date } : { itemId: preset.item });
  $('scrim').classList.add('on');
  $('sheet').classList.add('on');
  if (t) void loadNoteChips('f-note', t.bankText);
  if (!t) setTimeout(() => $('f-amt')?.focus(), 280);
}

// ---- "What was this for?" chips -----------------------------------------------------------

/** This household's latest notes per merchant text, fetched once per sheet session's merchant. */
const noteCache = new Map<string, string[]>();

/** Fill the chips under a note field (none: the slot stays empty). Never blocks the sheet. */
async function loadNoteChips(fieldId: string, merchant: string) {
  const m = merchant.trim();
  if (!m || store.data?.isSample) return;
  let notes = noteCache.get(m.toLowerCase());
  if (!notes) {
    try {
      notes = (await api.purchaseNotes(m)).notes;
    } catch {
      return; // no chips; the field works without them
    }
    noteCache.set(m.toLowerCase(), notes);
  }
  const slot = document.querySelector<HTMLElement>(`[data-note-chips="${CSS.escape(fieldId)}"]`);
  if (slot) slot.innerHTML = noteChips(fieldId, notes);
}

function openItemSheet(item: ItemDto | null, categoryId: string, prefill: Parameters<typeof vItemSheet>[3] = {}) {
  ui.sheet = { id: null, cat: categoryId, armed: false };
  ui.itemMonths = item ? [...item.months] : [];
  $('sheet').innerHTML = vItemSheet(store.data!, item, categoryId, prefill);
  $('scrim').classList.add('on');
  $('sheet').classList.remove('full'); // from the drawer: a normal sheet, so the scrim closes it
  $('sheet').classList.add('on');
}

/** "Which category is it in?" for a monthly charge whose merchant has none yet. Asked once. */
function openCandidateCategory(candidateId: string) {
  const d = store.data!;
  const name = d.insights.find((i) => i.id === `candidate:${candidateId}`)?.title.replace(/^Is (.*) a monthly charge\?$/, '$1') ?? 'it';
  ui.sheet = { id: null, cat: null, armed: false };
  $('sheet').innerHTML = vCandidateCategory(d, candidateId, name);
  $('scrim').classList.add('on');
  $('sheet').classList.add('on');
}

function openIncomeSheet(line: IncomeLineDto | null) {
  ui.sheet = { id: null, cat: null, armed: false };
  $('sheet').innerHTML = vIncomeSheet(store.data!, line);
  $('scrim').classList.add('on');
  $('sheet').classList.add('on');
}

function openGroupSheet(g: GroupDto | null) {
  ui.sheet = { id: null, cat: null, armed: false };
  $('sheet').innerHTML = vGroupSheet(store.data!, g);
  $('scrim').classList.add('on');
  $('sheet').classList.add('on');
}

/**
 * After a category or item pick in the add/edit sheet: fill the amount when it is empty or still
 * pre-filled (never once the user typed one). See prefillCents().
 */
function refill(itemId: string | null) {
  const input = document.getElementById('f-amt') as HTMLInputElement | null;
  if (!input || !ui.sheet) return;
  const next = refillAmount(input.value, input.dataset.autofill === '1', prefillCents(store.data!, ui.sheet.cat, itemId));
  if (!next) return;
  setCents(input, next.value);
  if (next.prefilled) input.dataset.autofill = '1';
  else delete input.dataset.autofill;
}

/** The yearly answers in the item editor, as typed. */
function readYearly() {
  const last = $<HTMLInputElement>('i-lastpaid').value || null;
  const amount = parseCents($<HTMLInputElement>('i-amt').value);
  return {
    amountCents: amount > 0 ? amount : 0,
    lastPaidOn: last,
    renewsOn: last ? null : $<HTMLInputElement>('i-renewson').value || null,
    renews: $<HTMLSelectElement>('i-renews').value === '1',
    spread: $<HTMLInputElement>('i-count-spread').checked,
    endsMonth: $<HTMLInputElement>('i-ends').value || null,
  };
}

/**
 * The item editor follows Repeats: a yearly item shows its own questions (no Due or month chips)
 * and one live summary line; "When does it renew?" only without a last charge. Spread turns
 * auto-log off, saying so when it was on.
 */
function syncYearlyFields(changed: string) {
  const box = document.getElementById('i-yearly');
  if (!box) return;
  const yearly = $<HTMLSelectElement>('i-cadence').value === 'yearly';
  box.hidden = !yearly;
  $('i-day-f').hidden = yearly;
  $('i-last-f').hidden = yearly;
  $('i-amt-label').textContent = yearly ? 'How much is the yearly charge?' : 'Amount';
  const y = readYearly();
  $('i-renewson-f').hidden = !!y.lastPaidOn;
  const spread = yearly && y.spread;
  const auto = document.getElementById('i-auto') as HTMLInputElement | null;
  if (auto) {
    const note = $('i-auto-note');
    if (spread) {
      if (auto.checked) note.textContent = AUTO_LOG_OFF_NOTE;
      else if (note.textContent !== AUTO_LOG_OFF_NOTE) note.textContent = SPREAD_AUTO_LOG_NOTE;
      auto.checked = false;
      auto.disabled = true;
    } else if (changed === 'i-count-whole' || changed === 'i-cadence') {
      auto.disabled = $<HTMLInputElement>('i-last').checked || (!yearly && $<HTMLSelectElement>('i-day').value === '');
      note.textContent = AUTO_LOG_NOTE;
    }
  }
  $('i-summary').textContent = yearlySummaryText(y, $('i-summary').dataset.today!);
}

/** "Also change the N earlier purchases": N per purchase and category, fetched once. */
const relatedCounts = new Map<string, number>();

/**
 * The correction options in the edit sheet: shown once the category or item differs from what the
 * purchase had, with the line saying what saving will do. N comes from the server.
 */
function updateLearn() {
  const box = document.getElementById('f-learn');
  const s = ui.sheet;
  if (!box || !s?.id) return;
  const t = store.data!.transactions.find((x) => x.id === s.id);
  if (!t) return;
  const newCat = s.cat !== t.categoryId;
  const newItem = !newCat && (s.item ?? null) !== (t.itemId ?? null);
  box.hidden = !newCat && !newItem;
  if (box.hidden) return;
  const item = newItem && s.item ? (store.data!.items.find((i) => i.id === s.item)?.name ?? null) : null;
  const key = `${t.id}|${s.cat}`;
  const others = newCat && s.cat ? relatedCounts.get(key) : 0;
  if (others === undefined && s.cat) {
    const cat = s.cat;
    void api
      .related(t.id, cat)
      .then((r) => {
        relatedCounts.set(key, r.count);
        if (ui.sheet?.id === t.id && ui.sheet.cat === cat) updateLearn();
      })
      .catch(() => relatedCounts.set(key, 0));
  }
  const n = others ?? 0;
  $('f-remember').closest('label')!.hidden = !s.cat;
  $('f-earlier-f').hidden = !newCat || !s.cat || n === 0;
  $('f-earlier-label').textContent = earlierLabel(n);
  $('f-learn-say').textContent = learnLine({
    merchant: t.merchant,
    category: s.cat ? catName(s.cat) : null,
    item,
    remember: $<HTMLInputElement>('f-remember').checked,
    earlier: !$('f-earlier-f').hidden && $<HTMLInputElement>('f-earlier').checked,
    others: n,
  });
}

/** The add sheet's "Split this bill": hidden for a yearly item spread over 12 months. */
function updateAddSplit() {
  const box = document.getElementById('f-split-box');
  if (!box || !ui.sheet) return;
  const item = ui.sheet.item ? store.data!.items.find((i) => i.id === ui.sheet!.item) : undefined;
  box.hidden = !!item?.spread;
  if (box.hidden) {
    $<HTMLInputElement>('f-split').checked = false;
    $('f-split-fields').hidden = true;
  }
}

/** One tap: log the planned amount (or an item's), then Undo. No other taps. */
async function logPlanned(categoryId: string, body: { date?: string; merchant?: string; itemId?: string }) {
  const k = categoryById(categoryId);
  let added = '';
  let cents = 0;
  await mutate(
    async () => {
      const r = await api.logPlanned(categoryId, body);
      added = r.transaction.id;
      cents = r.transaction.amountCents;
    },
    () => loggedText(cents, k?.name ?? 'the category'),
    () => api.deleteTransaction(added),
  );
}

/** The Accept sheet for a partner's split request: the request, the picked category and item. */
let accepting: AcceptState | null = null;

function renderAcceptSheet() {
  const html = accepting ? acceptSheetFor(store.data!, accepting) : '';
  if (!html) return closeSheet();
  $('sheet').innerHTML = html;
  // Chips from my own notes for this merchant (cached after the first load, so redraws are free).
  const r = store.data!.splitRequests.find((x) => x.id === accepting!.id);
  if (r) void loadNoteChips('sa-note', r.merchant);
}

/**
 * "You $50.01, Tiffany $50.00", as the percent is typed. The split sheet knows the purchase's
 * amount; the add sheet reads the amount being typed.
 */
function updateSplitShares() {
  const input = document.getElementById('sp-pct') as HTMLInputElement | null;
  if (!input) return;
  const pct = Number(input.value);
  // The Done sheet's split reads its own amount (d-amt); the add sheet reads f-amt and its tip.
  const field = input.dataset.amt ?? 'f-amt';
  const tip = field === 'f-amt' ? sheetTip() : null;
  const full = input.dataset.full
    ? Number(input.dataset.full)
    : parseCents((document.getElementById(field) as HTMLInputElement | null)?.value ?? '') + (typeof tip === 'number' ? tip : 0);
  $('sp-shares').textContent = full > 0 ? sharesText(full, pct, input.dataset.partner!) : 'Enter the amount above to see the two shares.';
  for (const chip of document.querySelectorAll<HTMLButtonElement>('[data-a="split-pct"]')) chip.setAttribute('aria-pressed', String(Number(chip.dataset.pct) === pct));
}

/**
 * The Done sheet's "Split with Tiffany": while the amount has not been typed (data-pristine), it
 * follows the switch and the percent, so the plan is my share ($140 at 70% moves $200; off, $140).
 */
function syncDoneSplitAmount() {
  const amt = document.getElementById('d-amt') as HTMLInputElement | null;
  const sw = document.getElementById('d-split') as HTMLInputElement | null;
  if (!amt || !sw) return;
  if (amt.dataset.pristine === '1') {
    const plan = Number(sw.dataset.plan);
    const pct = Number($<HTMLInputElement>('sp-pct').value);
    const cents = sw.checked && validSplitPercent(pct, false) ? grossFor(plan, pct) : plan;
    if (plan > 0) setCents(amt, (cents / 100).toFixed(2));
  }
  updateSplitShares();
}

// ---- Busy buttons (busy.ts): one loading state for every async button -------------------

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const asBusy = (b: HTMLElement) => b as unknown as BusyButton;

/** The inline error under a button: right after its row of buttons, made when first needed. */
const busyDeps: BusyDeps = {
  schedule: (fn, ms) => setTimeout(fn, ms),
  cancel: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  reducedMotion,
  showError(button, message) {
    const el = button as unknown as HTMLElement;
    const anchor = el.closest('.acts, .btns, .newall, .flowacts') ?? el;
    let err = anchor.nextElementSibling as HTMLElement | null;
    if (!err?.classList.contains('btnerr')) {
      if (!message) return;
      err = document.createElement('p');
      err.className = 'err btnerr';
      err.setAttribute('role', 'alert');
      anchor.after(err);
    }
    err.textContent = message ?? '';
    err.hidden = !message;
  },
};

/**
 * mutate() behind a button: it shows the loading state while `work` runs; a failure brings the
 * button back with the error under it (no toast). On success `before` runs (close a sheet, clear a
 * row's choices), the rows in `fade` fade out, then the overview reloads and the toast shows.
 */
async function busyMutate(
  b: HTMLElement,
  work: () => Promise<unknown>,
  opts: { success?: string | (() => string); undo?: () => Promise<unknown>; before?: () => void; fade?: string[] } = {},
): Promise<boolean> {
  const r = await runBusy(asBusy(b), work, busyDeps, errorText);
  if (!r.ok) return false;
  opts.before?.();
  const rows = (opts.fade ?? []).map((id) => document.querySelector(`[data-tx="${CSS.escape(id)}"]`)).filter((x): x is Element => !!x);
  const ms = fadeMs(reducedMotion());
  if (rows.length && ms) {
    for (const row of rows) row.classList.add('leaving');
    await new Promise((done) => setTimeout(done, ms));
  }
  await refresh();
  if (opts.success) toast(typeof opts.success === 'function' ? opts.success() : opts.success, opts.undo ? () => void mutate(opts.undo!) : undefined);
  return true;
}

// ---- New purchases rows and their Edit sheet (views/newcard.ts, views/newsheet.ts) --------

const newChoices = (): NewChoices => ({ picks: ui.picks, splits: ui.newSplits, tips: ui.newTips, funds: ui.newFunds, items: ui.newItems, fundOn: ui.newFundOn, notes: ui.newNotes, amounts: ui.newAmounts });

/** Forget what was picked on a row (after it is confirmed). */
function clearRow(id: string) {
  ui.picks.delete(id);
  ui.newSplits.delete(id);
  ui.newTips.delete(id);
  ui.newFunds.delete(id);
  ui.newItems.delete(id);
  ui.newFundOn.delete(id);
  ui.newNotes.delete(id);
  ui.newAmounts.delete(id);
}

/** The row's tip state, made from the purchase's own tip when there is none yet. */
function ensureTip(id: string): NewTip | undefined {
  const t = store.data?.transactions.find((x) => x.id === id);
  if (!t) return undefined;
  if (!ui.newTips.has(id)) ui.newTips.set(id, { tipCents: t.tipCents, tipped: t.usuallyTipped });
  return ui.newTips.get(id);
}

/** Draw the row's Edit sheet again (after a pick); nothing when it is closed. */
function renderNewSheet() {
  const s = ui.newSheet;
  const t = s && store.data?.transactions.find((x) => x.id === s.id);
  if (!s || !t) return;
  $('sheet').innerHTML = vNewSheet(store.data!, t, choicesOf(newChoices(), t.id), s, t.categoryId ? 'new-ok' : 'new-confirm');
  void loadNoteChips('ns-note', t.bankText);
}

/** After a pick on a row or in its sheet: the card and the sheet show it. */
function refreshNew() {
  render();
  renderNewSheet();
}

function openNewSheet(id: string) {
  const t = store.data!.transactions.find((x) => x.id === id);
  if (!t) return;
  ui.newSheet = newSheetState(t);
  // "Usually tipped": the Tip switch starts on, with no tip picked yet. A fund never starts on.
  const tip = startTip(store.data!, t);
  if (tip && !ui.newTips.has(id)) ui.newTips.set(id, tip);
  ui.sheet = { id: null, cat: null, armed: false };
  renderNewSheet();
  $('scrim').classList.add('on');
  $('sheet').classList.add('on');
}

/** The note typed in the sheet, as a PATCH: {} when unchanged. */
const rowNotePatch = (t: TransactionDto, c: RowChoices): { note?: string | null } => {
  const n = rowNote(t, c);
  return n !== (t.note ?? null) ? { note: n } : {};
};

/** The item picked in the sheet, as a PATCH: {} when unchanged. */
function rowItem(t: TransactionDto, cat: string | null): { itemId?: string | null } {
  if (!ui.newItems.has(t.id)) return {};
  const item = ui.newItems.get(t.id) ?? null;
  return item !== (cat === t.categoryId ? t.itemId : null) ? { itemId: item } : {};
}

/**
 * Confirm one row (on the card or in its sheet): its category, item, tip and fund in one PATCH
 * (new-confirm files a purchase with no category; new-ok marks a filed one seen, after any change),
 * then the split request. The row fades out once saved.
 */
function confirmRow(b: HTMLElement, action: 'new-confirm' | 'new-ok') {
  const id = b.dataset.id!;
  const t = store.data!.transactions.find((x) => x.id === id);
  if (!t) return;
  const c = choicesOf(newChoices(), id);
  const cat = rowCategory(t, c);
  if (action === 'new-confirm' && !cat) return;
  // "Paid from savings" on with no fund chosen: nothing is saved until one is.
  if (fundMissing(t, c)) return busyDeps.showError(asBusy(b), CHOOSE_FUND);
  // The tip goes with the confirm, before the split (which shares the tipped total).
  // An auto-logged charge's amount, as typed in its Edit sheet (the edit sheet's PATCH rules).
  const amount = rowAmountPatch(t, c);
  if (amount === false) return busyDeps.showError(asBusy(b), AMOUNT_NEEDED);
  const tip = rowTip(t, b);
  if (tip === false) return;
  const split = rowSplit(id, b);
  if (split === false) return;
  const fund = rowFund(t);
  const item = rowItem(t, cat);
  const note = rowNotePatch(t, c);
  const recat = cat && cat !== t.categoryId ? { categoryId: cat, rememberForMerchant: true } : {};
  const changes = { ...recat, ...item, ...amount, ...tip, ...fund, ...note };
  // A new total (amount or tip) on a row whose split is already waiting: the PATCH cancels it and it goes again
  // (same path as the edit sheet, its original note); if it could not, nothing was saved and the
  // reason shows under the button.
  let resent = '';
  const resend = (r: SplitAfterEdit) =>
    resendAfterEdit(r, (pct, n) => api.sendSplit(id, pct, n), partnerName(), splitRefusal, 'Filed').then((text) => (resent = text ? ` ${text}` : ''));
  const confirm =
    action === 'new-confirm'
      ? () => api.updateTransaction(id, { categoryId: cat!, rememberForMerchant: true, ...item, ...amount, ...tip, ...fund, ...note }).then(resend)
      : Object.keys(changes).length
        ? () => api.updateTransaction(id, changes).then(resend).then(() => api.reviewTransactions([id]))
        : () => api.reviewTransactions([id]);
  const fromSheet = b.dataset.from === 'sheet';
  const charge = amount.amountCents ?? t.amountCents;
  const extras = `${amount.amountCents ? ` Amount now ${money(amount.amountCents, 2)}.` : ''}${fund.fundedFromCategoryId ? ` Paid from ${catName(fund.fundedFromCategoryId)}.` : ''}${tip.tipCents ? ` Tip added, total ${money(charge + tip.tipCents, 2)}.` : ''}`;
  let tail = '';
  return busyMutate(b, async () => (tail = await confirmThenSplit(confirm, split, partnerName(), splitRefusal)), {
    before: () => {
      clearRow(id);
      if (fromSheet) closeSheet();
    },
    fade: [id],
    success:
      action === 'new-confirm'
        ? () => `Filed ${t.merchant} under ${catName(cat)}.${extras}${tail}${resent}`
        : split || extras || recat.categoryId || resent
          ? () => `Marked as seen.${extras}${tail}${resent}`
          : undefined,
  });
}

/**
 * "Confirm all N": each row's category (and item) as picked or filed; never a split, tip or fund,
 * and the toast names the rows that had one (bulkFiledText()).
 */
function confirmAllRows(b: HTMLElement, action: 'new-confirm-all' | 'new-ok-all') {
  const ids = b.dataset.ids!.split(',').filter(Boolean);
  const txs = ids.map((id) => store.data!.transactions.find((t) => t.id === id)).filter((t): t is TransactionDto => !!t);
  const merchantOf = (id: string) => txs.find((t) => t.id === id)?.merchant ?? 'a purchase';
  const notSplit = ids.filter((id) => ui.newSplits.has(id)).map(merchantOf);
  const notTipped = ids.filter((id) => ui.newTips.get(id)?.tipCents && ui.newTips.get(id)!.tipCents !== txs.find((t) => t.id === id)?.tipCents).map(merchantOf);
  const notFunded = ids.filter((id) => ui.newFunds.get(id)).map(merchantOf);
  // An amount typed in a row's Edit sheet is a correction, not a split or a tip: it is saved too.
  // Checked for every row first, so a bad one saves nothing. A new amount on a purchase with an
  // open split would need it sent again, and Confirm all never sends a split: that row's own
  // Confirm does it.
  const bad = txs.find((t) => rowAmountPatch(t, choicesOf(newChoices(), t.id)) === false);
  if (bad) return busyDeps.showError(asBusy(b), `${AMOUNT_NEEDED.slice(0, -1)} for ${bad.merchant}.`);
  const shared = txs.find((t) => openSentSplit(t) && 'amountCents' in (rowAmountPatch(t, choicesOf(newChoices(), t.id)) || {}));
  if (shared) return busyDeps.showError(asBusy(b), AMOUNT_SPLIT_ALONE(shared.merchant));
  const work = async () => {
    for (const t of txs) {
      const c = choicesOf(newChoices(), t.id);
      const cat = rowCategory(t, c);
      const item = rowItem(t, cat);
      // A note typed in the row's Edit sheet is the user's own words: saved with it.
      const note = rowNotePatch(t, c);
      const amount = rowAmountPatch(t, c) || {};
      if (action === 'new-confirm-all' && cat) await api.updateTransaction(t.id, { categoryId: cat, rememberForMerchant: true, ...item, ...amount, ...note });
      else if (cat && (cat !== t.categoryId || 'itemId' in item || 'note' in note || 'amountCents' in amount))
        await api.updateTransaction(t.id, { ...(cat !== t.categoryId ? { categoryId: cat, rememberForMerchant: true } : {}), ...item, ...amount, ...note });
    }
    if (action === 'new-ok-all') await api.reviewTransactions(ids);
  };
  return busyMutate(b, work, {
    before: () => ids.forEach(clearRow),
    fade: ids,
    success: bulkFiledText(ids.length, notSplit, notTipped, notFunded),
  });
}

// ---- Tips (views/tips.ts): the sheet's fields are read on save; a row's live in ui.newTips ----

const centsOr = (v: string) => {
  const c = parseCents(v);
  return c > 0 ? c : null;
};

/** The sheet's tip as saved: null for none, 'below' when the final total is under the charge. */
function sheetTip(): number | null | 'below' {
  const fields = document.getElementById('f-tip-fields');
  if (!fields || fields.hidden) return null;
  if ($<HTMLInputElement>('f-total').dataset.below) return 'below';
  return centsOr($<HTMLInputElement>('f-tip').value);
}

/**
 * Keep the sheet's tip fields in step: `from` is the field just typed in (the other one follows),
 * then the total line and the quick picks. The amount field is the bank charge.
 */
function syncSheetTip(from: 'tip' | 'total' | 'amount') {
  const tipEl = document.getElementById('f-tip') as HTMLInputElement | null;
  if (!tipEl) return;
  const totalEl = $<HTMLInputElement>('f-total');
  const charge = centsOr($<HTMLInputElement>('f-amt').value);
  delete totalEl.dataset.below;
  if (from === 'total') {
    const total = centsOr(totalEl.value);
    const tip = charge && total ? tipFromTotal(charge, total) : null;
    if (charge && total && tip === null) totalEl.dataset.below = '1';
    else tipEl.value = tip ? (tip / 100).toFixed(2) : '';
  } else {
    const tip = centsOr(tipEl.value);
    totalEl.value = charge && tip ? ((charge + tip) / 100).toFixed(2) : '';
  }
  const tip = centsOr(tipEl.value);
  $('f-tip-total').textContent = tipLine(charge, { tipCents: tip, below: !!totalEl.dataset.below });
  for (const chip of document.querySelectorAll<HTMLButtonElement>('#sheet [data-a="tip-pct"]'))
    chip.setAttribute('aria-pressed', String(!!charge && tip === tipFor(charge, Number(chip.dataset.pct))));
  updateSplitShares();
}

/** The add sheet's opener follows the category and the name typed: "Add tip?" when a tip is likely. */
function updateTipOpener() {
  const opener = document.getElementById('f-tip-opener');
  if (!opener || opener.hidden || !ui.sheet || ui.sheet.id) return;
  const typed = (document.getElementById('f-mer') as HTMLInputElement | null)?.value ?? '';
  opener.innerHTML = tipOpener(tipProminent(store.data!, null, ui.sheet.cat, typed), 'data-a="tip-open"');
}

/** A New purchases row's tip patch: {} when unchanged, false when its total is below the charge. */
function rowTip(t: TransactionDto, b: HTMLElement): { tipCents?: number | null; usuallyTipped?: boolean } | false {
  const tip = ui.newTips.get(t.id);
  if (!tip) return {};
  if (tip.below) {
    busyDeps.showError(asBusy(b), TIP_BELOW_CHARGE);
    return false;
  }
  return {
    ...(tip.tipCents !== (t.tipCents ?? null) ? { tipCents: tip.tipCents } : {}),
    ...(tip.tipped !== t.usuallyTipped ? { usuallyTipped: tip.tipped } : {}),
  };
}

/** A New purchases row's "Paid from savings" patch: {} when unchanged ('' takes it off). */
function rowFund(t: TransactionDto): { fundedFromCategoryId?: string | null } {
  const fund = ui.newFunds.get(t.id);
  if (fund === undefined || fund === (t.fundedFromCategoryId ?? '')) return {};
  return { fundedFromCategoryId: fund || null };
}

/** The sheet's "Paid from savings", drawn again after a pick (open: the chips show). */
function redrawSheetFund(picked: string) {
  const box = document.getElementById('f-fund-box');
  if (!box || !ui.sheet) return;
  const t = ui.sheet.id ? (store.data!.transactions.find((x) => x.id === ui.sheet!.id) ?? null) : null;
  box.outerHTML = sheetFundBlock(store.data!, t, picked, true);
}

/** After a row's tip changes: its fields, total line and split shares, in place (no re-render). */
function syncRowTip(id: string, from: 'tip' | 'total') {
  const tip = ui.newTips.get(id);
  const t0 = store.data!.transactions.find((x) => x.id === id);
  if (!tip || !t0) return;
  // The bank charge: an amount typed in the sheet (auto-logged charges), else the purchase's own.
  const t = { ...t0, amountCents: rowCharge(t0, choicesOf(newChoices(), id)) };
  const q = (attr: string) => document.querySelector<HTMLInputElement>(`[${attr}="${CSS.escape(id)}"]`);
  for (const el of [q('data-new-tip'), q('data-new-total')]) if (el) el.dataset.charge = String(t.amountCents);
  if (from === 'tip') {
    const total = q('data-new-total');
    if (total) total.value = tip.tipCents ? ((t.amountCents + tip.tipCents) / 100).toFixed(2) : '';
  } else {
    const tipEl = q('data-new-tip');
    if (tipEl && !tip.below) tipEl.value = tip.tipCents ? (tip.tipCents / 100).toFixed(2) : '';
  }
  const line = document.querySelector(`[data-new-tip-total="${CSS.escape(id)}"]`);
  if (line) line.textContent = tipLine(t.amountCents, tip);
  for (const chip of document.querySelectorAll<HTMLButtonElement>(`[data-a="new-tip-pct"][data-id="${CSS.escape(id)}"]`))
    chip.setAttribute('aria-pressed', String(tip.tipCents === tipFor(t.amountCents, Number(chip.dataset.pct))));
  // "Split this bill" shares the tipped total.
  const pct = q('data-new-split-pct');
  if (pct) {
    pct.dataset.full = String(t.amountCents + (tip.tipCents ?? 0));
    const shares = document.querySelector(`[data-new-split-shares="${CSS.escape(id)}"]`);
    if (shares) shares.textContent = sharesText(Number(pct.dataset.full), Number(pct.value), pct.dataset.partner!);
  }
}

/** A New purchases row's split request, null when "Split this bill" is off, false when its share is not valid. */
function rowSplit(id: string, b: HTMLElement): (() => Promise<unknown>) | null | false {
  const s = ui.newSplits.get(id);
  if (!s) return null;
  if (!validPercent(s.pct)) {
    busyDeps.showError(asBusy(b), SPLIT_PERCENT_HINT);
    return false;
  }
  // The row's "What was this for?", as its Edit sheet shows it with "Tiffany will see this." ('' none).
  const t = store.data!.transactions.find((x) => x.id === id);
  const note = t ? rowSplitNote(t, choicesOf(newChoices(), id)) : '';
  return () => api.sendSplit(id, s.pct, note);
}

const partnerName = () => store.data?.partner.linked?.name ?? 'your partner';

/**
 * A month other than the calendar month (an old month, or the active one being finished): one-tap
 * Done and logging are dated on its last day, Mark not done looks in it. Nothing in the calendar month.
 */
const viewedPastMonth = () => (store.data && !store.data.month.isCurrent ? store.data.month.key : undefined);
const onViewedDay = (): { date?: string } => {
  const m = viewedPastMonth();
  return m ? { date: isoDay(m, store.data!.month.today) } : {};
};

/**
 * Deleting a purchase, the one path (Activity's edit sheet and a New purchases row alike): a soft
 * delete on the server (DELETE /api/transactions/:id, the split rules in split-delete.ts), so Undo
 * brings back the exact row, source included. A waiting split request is cancelled with it; Undo
 * reopens it when it still can, and says so either way (never a split silently lost).
 */
function deletion(t: TransactionDto, said: (splitCancelled: string) => string) {
  let splitCancelled = '';
  return {
    work: async () => {
      splitCancelled = (await api.deleteTransaction(t.id))?.splitCancelled ?? '';
    },
    success: () => said(splitCancelled),
    undo: async () => {
      const r = await api.restoreTransaction(t.id);
      const said = r.splitRestored ?? r.splitNotRestored;
      if (said) toast(`Restored ${t.merchant}. ${said}`);
    },
  };
}

/**
 * "Tiffany will see this." under "What was this for?": shown while the split is on (its switch, or
 * fixed by the sheet: `data-on`) and the note is not empty, as views/notes.ts noteSeesLine() draws it.
 */
function syncNoteSees() {
  for (const line of document.querySelectorAll<HTMLElement>('[data-note-sees]')) {
    const field = document.getElementById(line.dataset.noteSees!) as HTMLInputElement | null;
    const on = line.dataset.switch ? !!(document.getElementById(line.dataset.switch) as HTMLInputElement | null)?.checked : line.dataset.on === '1';
    line.hidden = !seesShown(on, field?.value);
  }
}

/** Why a split was refused, in a few words: the link gone (404), else the server's own reason. */
const splitRefusal = (e: unknown) => (e instanceof ApiError && e.status === 404 ? SPLIT_NO_LINK : errorText(e));

function closeSheet() {
  const back = ui.drawerReturn;
  if (back && !ui.drawer) {
    ui.drawerReturn = null;
    return openDrawer(back.id, back.draft);
  }
  ui.drawer = null;
  $('scrim').classList.remove('on');
  $('sheet').classList.remove('on', 'full');
  ui.sheet = null;
  ui.newSheet = null;
}

/** A fresh Idempotency-Key for one add-sheet submit. */
const newSubmitKey = () => crypto.randomUUID();

async function saveSheet(b: HTMLElement) {
  const d = store.data!;
  const sheet = ui.sheet!;
  const amountCents = parseCents($<HTMLInputElement>('f-amt').value);
  if (!(amountCents > 0)) {
    $('f-err').textContent = 'Enter an amount above $0.';
    return;
  }
  const merchant = $<HTMLInputElement>('f-mer').value.trim() || 'Purchase';
  const date = clampDate(d, $<HTMLInputElement>('f-date').value);
  const categoryId = sheet.cat;
  // "Add tip": on top of the amount (the bank charge); a final total below it is refused.
  const tip = sheetTip();
  if (tip === 'below') {
    $('f-err').textContent = TIP_BELOW_CHARGE;
    return;
  }
  const tippedBox = document.getElementById('f-tipped') as HTMLInputElement | null;
  // "Paid from savings": the fund's id, '' for none (absent: not offered here).
  const fundField = document.getElementById('f-fund') as HTMLInputElement | null;
  // "What was this for?": trimmed, empty is none.
  const note = cleanNote((document.getElementById('f-note') as HTMLInputElement | null)?.value);
  if (sheet.id) {
    const id = sheet.id;
    const t = d.transactions.find((x) => x.id === id);
    // The tip and "Usually tipped": sent only when they changed.
    const tipPatch = t
      ? {
          ...(document.getElementById('f-tip-box') && tip !== (t.tipCents ?? null) ? { tipCents: tip } : {}),
          ...(tippedBox && tippedBox.checked !== t.usuallyTipped ? { usuallyTipped: tippedBox.checked } : {}),
          ...(fundField && fundField.value !== (t.fundedFromCategoryId ?? '') ? { fundedFromCategoryId: fundField.value || null } : {}),
          // The note: sent only when it changed, so other edits never touch it.
          ...(note !== (t.note ?? null) ? { note } : {}),
        }
      : {};
    // The name is display only; send it only when it changed.
    const name = merchant.slice(0, 60);
    const rename = t && name !== t.merchant ? { displayName: name, everyPurchase: $<HTMLInputElement>('f-every').checked } : {};
    // The Item picker: send it only when it changed (on a new category, only when one was picked).
    const item = sheet.item ?? null;
    const pick = t && item !== (t.categoryId === categoryId ? t.itemId : null) ? { itemId: item } : {};
    // The correction options, when the category or item changed.
    const learn = document.getElementById('f-learn');
    const earlierBox = document.getElementById('f-earlier-f');
    const options =
      learn && !learn.hidden
        ? {
            rememberForMerchant: $<HTMLInputElement>('f-remember').checked,
            ...(earlierBox && !earlierBox.hidden && $<HTMLInputElement>('f-earlier').checked ? { applyToEarlier: true } : {}),
          }
        : {};
    let also = 0;
    let splitNote = '';
    // The sheet stays open while it saves; a failure shows under Save and nothing closes.
    await busyMutate(b, async () => {
        const r = await api.updateTransaction(id, { amountCents, date, categoryId, ...rename, ...pick, ...options, ...tipPatch });
        also = r.alsoChanged ?? 0;
        // A new total (amount or tip) cancelled a waiting split: it goes again with its original note.
        splitNote = await resendAfterEdit(r, (pct, note) => api.sendSplit(id, pct, note), partnerName(), splitRefusal, 'Saved');
      },
      { before: closeSheet, success: () => splitNote || `Saved changes to ${name}${also ? ` and ${also} earlier ${also === 1 ? 'purchase' : 'purchases'}` : ''}` },
    );
  } else {
    // "Split this bill": checked before anything is saved.
    const split = (document.getElementById('f-split') as HTMLInputElement | null)?.checked ?? false;
    const pct = split ? Number($<HTMLInputElement>('sp-pct').value) : 0;
    if (split && !validSplitPercent(pct)) {
      $('f-err').textContent = SPLIT_PERCENT_HINT;
      return;
    }
    // The purchase's own "What was this for?" (saved with it, and shown with "Tiffany will see this.").
    const splitNoteText = split ? splitNoteOf(note) : '';
    const partner = d.partner.linked?.name ?? 'your partner';
    let newId = '';
    let splitNote = '';
    const reviewId = sheet.reviewId;
    await busyMutate(b, async () => {
        // One Idempotency-Key per submit, kept for its retries: the server gives back the purchase
        // it already made instead of logging a second one (and the client skips the call too).
        const key = (sheet.submitKey ??= newSubmitKey());
        newId = sheet.createdId ??= (
          await api.addTransaction(
            {
              merchant,
              amountCents,
              categoryId,
              date,
              ...(typeof tip === 'number' ? { tipCents: tip } : {}),
              ...(tippedBox?.checked ? { usuallyTipped: true } : {}),
              ...(fundField?.value ? { fundedFromCategoryId: fundField.value } : {}),
              ...(note ? { note } : {}),
            },
            key,
          )
        ).transaction.id;
        // An item picked in the sheet (or preset by an item row's "+") wins over Hush's own match.
        const item = sheet.item && store.data!.items.find((i) => i.id === sheet.item && i.categoryId === categoryId);
        if (item) await api.updateTransaction(newId, { itemId: item.id });
        if (reviewId) await api.resolveReviewItem(reviewId);
        // The purchase is saved either way; a refused split leaves it in full, and says why.
        if (split) {
          try {
            await api.sendSplit(newId, pct, splitNoteText);
            splitNote = ` ${SPLIT_SENT(partner)}`;
          } catch (e) {
            splitNote = ` ${SPLIT_NOT_SENT('Saved', splitRefusal(e))}`;
          }
        }
      },
      {
        before: closeSheet,
        success: () => `Logged ${money(amountCents + (typeof tip === 'number' ? tip : 0), 2)}${categoryId ? ' to ' + catName(categoryId) : ''}.${splitNote}`,
        undo: () => api.deleteTransaction(newId),
      },
    );
  }
}

// ---- Events --------------------------------------------------------------------------

let pastePreview: PastePreviewResponse | null = null;

function openPasteSheet() {
  pastePreview = null;
  ui.sheet = { id: null, cat: null, armed: false };
  $('sheet').innerHTML = vPasteSheet(null);
  $('scrim').classList.add('on');
  $('sheet').classList.add('on');
  setTimeout(() => $('p-text')?.focus(), 280);
}

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const actions: Record<string, (b: HTMLElement) => void | Promise<unknown>> = {
  // A tab-bar tap: that tab's top level, from any screen.
  tab: (b) => {
    activitySearch = '';
    go({ to: 'tab', tab: b.dataset.tab as Exclude<Tab, 'settings'> });
  },
  settings: () => go({ to: 'settings' }),
  'due-toggle': () => {
    try {
      localStorage.setItem(DUE_KEY, dueOpen() ? '0' : '1');
    } catch {
      /* not remembered in a private window; it still toggles below for this view */
    }
    render();
  },
  'setup-snooze': () => mutate(() => api.dismissSetupCard(), 'Hidden for 3 days. Add a card any time from Settings, Cards and banks.'),
  // Settings: a row of the menu opens its screen; Back comes back to the menu where it was.
  'settings-page': (b) => openSettingsPage(b.dataset.page as SettingsPage),
  'card-open': (b) => openRawSheet(vCardSheet(store.data!, b.dataset.id!)),
  'bank-open': (b) => openRawSheet(vBankSheet(store.data!, b.dataset.key!)),
  // The add-a-card flow (web/src/views/card-flow.ts).
  'flow-open': () => openFlow(),
  'flow-close': () => closeFlow(),
  'flow-back': () => {
    const f = ui.cardFlow;
    if (!f || !f.trail.length) return;
    f.step = f.trail.pop()!;
    renderFlow();
  },
  'flow-gate': (b) => flowGo(afterGate(b.dataset.answer as GateAnswer, store.data!.cardsBanks.gmail.length)),
  'flow-alerts-on': () => flowGo(afterAlerts(store.data!.cardsBanks.gmail.length)),
  'flow-gmail': (b) => {
    const f = ui.cardFlow!;
    const address = b.dataset.gmail || null;
    f.gmail = address;
    f.gmailOther = !address;
    flowGo(afterGmail(!!address && store.data!.cardsBanks.gmail.some((g) => g.address === address && g.verified)));
  },
  'flow-verified': () => {
    const f = ui.cardFlow!;
    f.verifiedHere = true;
    // "A different Gmail address": the one Gmail's confirmation just taught Hush.
    if (f.gmailOther && !f.gmail) f.gmail = store.data!.cardsBanks.gmail.find((g) => !f.knownAtStart.includes(g.address))?.address ?? null;
    if (!f.example || !f.compare) return flowGo('example');
    flowGo(afterCompare({ kind: f.compare.result.kind, needsVerify: false, alertsShown: f.alertsShown }));
  },
  'flow-next': (b) => flowGo(b.dataset.to as FlowStep),
  'flow-add': async (b) => {
    const f = ui.cardFlow!;
    if (!f.example) return;
    f.nickname = ($<HTMLInputElement>('flow-nick')?.value ?? '').trim();
    // The step's button shows the loading state; a refusal shows under it.
    const r = await runBusy(asBusy(b), () => api.flowAdd(f.example!.id, f.gmail, f.nickname || null), busyDeps, errorText);
    if (!r.ok) return;
    f.compare = r.value;
    f.cardId = r.value.cardId;
    if (!f.gmail && r.value.gmail) f.gmail = r.value.gmail;
    await refresh({ silent: true });
    const c = f.compare!;
    flowGo(afterCompare({ kind: c.result.kind, needsVerify: !c.gmailVerified && !f.verifiedHere, alertsShown: f.alertsShown }));
  },
  'flow-help': () => toast('Help for this step is coming soon.'),
  filter: (b) => go({ to: 'activity', filter: b.dataset.cat! }),
  // The blue +: on a category screen it logs into that category (still changeable); on a
  // one-amount set-aside category it is "Different amount". Elsewhere a new purchase.
  add: () => {
    const k = ui.categoryId ? categoryById(ui.categoryId) : undefined;
    if (k?.kind === 'set_aside' && !k.itemized) return actions['item-done-other']({ dataset: { cat: k.id } } as unknown as HTMLElement);
    return openSheet(null, undefined, k && k.kind !== 'set_aside' ? { cat: k.id } : {});
  },
  // "Paid $250" on Overview and "Log planned amount ($250)" on a category screen: today, Undo.
  'log-planned': (b) => logPlanned(b.dataset.cat!, onViewedDay()),
  // The "+" on an item row: a purchase for this item, category and item picked, amount empty.
  'item-log': (b) => {
    const item = store.data!.items.find((i) => i.id === b.dataset.id);
    if (item) openSheet(null, undefined, { cat: item.categoryId, item: item.id });
  },
  edit: (b) => openSheet(b.dataset.id!),
  close: () => requestClose(),
  pick: (b) => {
    const s = ui.sheet!;
    s.cat = s.cat === b.dataset.cat ? null : b.dataset.cat!;
    document
      .querySelectorAll<HTMLElement>('#sheet [data-a="pick"]')
      .forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.cat === s.cat)));
    refill(null);
    updateAddSplit();
    updateTipOpener();
    // The Item picker follows the category: its items, nothing picked yet.
    const slot = document.getElementById('f-item-slot');
    if (slot) {
      const t = s.id ? store.data!.transactions.find((x) => x.id === s.id) : undefined;
      s.item = t && t.categoryId === s.cat ? t.itemId : null;
      slot.innerHTML = itemPicker(store.data!, s.cat, s.item ?? null);
    }
    updateLearn();
  },
  'pick-item': (b) => {
    const s = ui.sheet!;
    s.item = b.dataset.item || null;
    refill(s.item);
    updateAddSplit();
    document.querySelectorAll<HTMLElement>('#sheet [data-a="pick-item"]').forEach((x) => x.setAttribute('aria-pressed', String((x.dataset.item || null) === s.item)));
    updateLearn();
  },
  'make-item': () => {
    const t = store.data!.transactions.find((x) => x.id === ui.sheet?.id);
    if (!t?.categoryId) return;
    openItemSheet(null, t.categoryId, { name: t.merchant, amountCents: t.amountCents, dueDay: Number(t.date.slice(8, 10)), fromTransactionId: t.id });
  },
  // Only the primary number on each row and group header changes; render() keeps the scroll.
  // Settings, Notifications. Permission is asked only here, on the tap.
  'push-enable': () =>
    pushAction(async () => {
      const key = pushUi?.server?.publicKey;
      if (!key) return;
      const sub = await subscribeDevice(key);
      if (sub === 'denied' || sub === 'default') return;
      const r = await api.pushSubscribe(sub);
      storeDeviceId(r.deviceId);
      pushUi!.deviceId = r.deviceId;
      return r;
    }),
  'push-disable': () =>
    pushAction(async () => {
      const id = pushUi?.deviceId;
      await unsubscribeDevice().catch(() => {});
      storeDeviceId(null);
      pushUi!.deviceId = null;
      return id ? api.pushRemove(id) : api.pushView();
    }, 'Notifications are off on this device.'),
  'push-remove': (b) => pushAction(() => api.pushRemove(b.dataset.id!), 'That device will not get notifications.'),
  'push-test': () => pushAction(() => api.pushTest(), 'Test sent. It should appear in a few seconds.'),
  // A suggestion under "What was this for?": fills the field (and a New purchases row's choice).
  'note-chip': (b) => {
    const field = document.getElementById(b.dataset.for!) as HTMLInputElement | null;
    if (!field) return;
    field.value = b.dataset.note ?? '';
    // Through the input path, so the row's choice and a split's note field follow it too.
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.focus();
  },
  'row-mode': (b) => {
    rowMode = parseRowMode(b.dataset.mode);
    writeRowMode(storage(), rowMode);
    render();
  },
  category: (b) => go({ to: 'category', id: b.dataset.cat! }),
  // One level up (nav.ts): the Splits screen, a Settings page, a category screen, then Settings
  // to the tab it was opened from. The screen underneath comes back where it was.
  back: () => go({ to: 'back' }),
  'settle-screen': () => {
    // History opens closed, newest five, each time the screen is opened.
    ui.settleHistory = { open: new Set(), older: false };
    go({ to: 'splits' });
    return loadSettle();
  },
  // Settle up in one step: the ticked lines are settled at once, on both sides. The button names
  // who paid ("Mark as paid by Tiffany", "I paid Tiffany"); the partner can dispute it for 7 days.
  'settle-send': async (b) => {
    if (!settleData) return;
    const ids = tickedLines(settleData, ui.settleOff).map((l) => l.id);
    if (!ids.length) return;
    const net = Number(b.dataset.net);
    await busyMutate(
      b,
      async () => {
        settleData = (await api.proposeSettle(ids, net)).settle;
      },
      { success: () => `Settled. ${settleData?.partnerName ?? 'Your partner'} can see it in Hush.` },
    );
  },
  // History on the Splits screen: a row opens and closes in place; "Show older" shows the rest.
  'settle-hist': (b) => {
    const id = b.dataset.id!;
    const open = ui.settleHistory.open;
    if (open.has(id)) open.delete(id);
    else open.add(id);
    render();
    document.querySelector<HTMLElement>(`[data-a="settle-hist"][data-id="${CSS.escape(id)}"]`)?.focus();
  },
  'settle-older': () => {
    ui.settleHistory.older = true;
    render();
  },
  'settle-dispute': (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again to dispute';
      return;
    }
    return settleChange(b, () => api.disputeSettle(b.dataset.id!), 'Disputed. Those splits are open again.');
  },
  'learned-open': () => {
    learnedData = null;
    go({ to: 'settings', page: 'learned' });
    return loadLearned();
  },
  'learn-forget': (b) => {
    if (b.dataset.armed !== '1') {
      b.dataset.armed = '1';
      b.textContent = 'Tap again to forget';
      return;
    }
    return learnedChange(() => api.forgetRule(b.dataset.key!), `Forgotten. The next purchase from ${b.dataset.name} asks again.`);
  },
  'learn-forget-merge': (b) => learnedChange(() => api.forgetMerge(b.dataset.key!), 'Forgotten. Hush will ask next time.'),
  'learn-forget-monthly': (b) => learnedChange(() => api.forgetMonthly(b.dataset.id!, b.dataset.key!), 'Forgotten. Its next charge is not linked by itself.'),
  'cat-drawer': (b) => openDrawer(b.dataset.id!),
  'cat-fold': (b) => {
    const k = categoryById(b.dataset.id!);
    if (!k) return;
    k.collapsed = !k.collapsed; // shown at once, remembered for the household
    render();
    void api.updateCategory(k.id, { collapsed: k.collapsed }).catch((e) => toast(errorText(e)));
  },
  'drawer-mode': (b) => {
    const dr = ui.drawer!;
    readDraft();
    dr.draft = switchMode(categoryById(dr.id)!, dr.draft, b.dataset.mode === 'list');
    renderDrawer();
  },
  'drawer-room': () => {
    readDraft();
    ui.drawer!.draft.roomShown = true;
    renderDrawer();
    $<HTMLInputElement>('dr-room')?.focus();
  },
  'drawer-item': (b) => itemFromDrawer(store.data!.items.find((i) => i.id === b.dataset.id) ?? null),
  'drawer-item-add': () => itemFromDrawer(null),
  'drawer-keep': () => {
    ui.drawer!.confirm = false;
    renderDrawer();
  },
  'drawer-discard': () => {
    const after = ui.drawer?.after;
    closeDrawer();
    after?.();
  },
  'drawer-purchases': () => {
    const id = ui.drawer!.id;
    requestClose(() => go({ to: 'activity', filter: id }));
  },
  /** "Save changes": name, amount, plan mode and group in one request. */
  'drawer-save': async (b) => {
    readDraft();
    const dr = ui.drawer!;
    const k = categoryById(dr.id)!;
    const r = drawerPatch(store.data!, k, dr.draft);
    if ('error' in r) return renderDrawer(r.error);
    if (!Object.keys(r.patch).length) return closeDrawer();
    // A refusal (e.g. a group its items do not allow) shows under Save; nothing was saved.
    await busyMutate(b, () => api.updateCategory(k.id, r.patch), { before: closeDrawer, success: `Saved ${dr.draft.name.trim()}` });
  },
  'item-add': (b) => openItemSheet(null, b.dataset.cat!),
  'item-edit': (b) => {
    const i = store.data!.items.find((x) => x.id === b.dataset.id);
    if (i) openItemSheet(i, i.categoryId);
  },
  // Done: the planned amount, today. Undo removes it again ("Mark not done").
  // Done on a transfer item (data-id), or on a plain category of money set aside (data-cat).
  'item-done': (b) => {
    const { id, cat } = b.dataset;
    // A split transfer item (70/30): the sheet, so the shares are seen before a request goes.
    const splitItem = id ? store.data!.items.find((x) => x.id === id) : undefined;
    if (splitItem?.splitPercent != null && splitsEnabled(store.data!.partner) && !splitItem.spread) return actions['item-done-other'](b);
    const name = (cat ? categoryById(cat)?.name : store.data!.items.find((x) => x.id === id)?.name) ?? 'Transfer';
    // Undo removes exactly the entry this Done added.
    let added = '';
    return mutate(
      async () => (added = (await (cat ? api.markCategoryDone(cat, onViewedDay()) : api.markDone(id!, onViewedDay()))).transaction.id),
      `${name} done`,
      () => api.deleteTransaction(added),
    );
  },
  'item-done-other': (b) => {
    const { id, cat } = b.dataset;
    const target = cat ? categoryById(cat) : store.data!.items.find((x) => x.id === id);
    if (!target) return;
    ui.sheet = { id: null, cat: cat ?? (target as ItemDto).categoryId, armed: false };
    $('sheet').innerHTML = vDoneSheet(store.data!, target);
    $('scrim').classList.add('on');
    $('sheet').classList.add('on');
    setTimeout(() => $('d-amt')?.focus(), 280);
  },
  'item-done-save': (b) => {
    const d = store.data!;
    const id = document.querySelector<HTMLInputElement>('#d-id')?.value;
    const cat = document.querySelector<HTMLInputElement>('#d-cat')?.value;
    const amountCents = parseCents($<HTMLInputElement>('d-amt').value);
    if (!(amountCents > 0)) {
      $('f-err').textContent = 'Enter an amount above $0.';
      return;
    }
    const date = clampDate(d, $<HTMLInputElement>('d-date').value);
    const name = (cat ? categoryById(cat)?.name : d.items.find((x) => x.id === id)?.name) ?? 'Transfer';
    // "Split with Tiffany" (a transfer item): checked before anything is saved.
    const split = (document.getElementById('d-split') as HTMLInputElement | null)?.checked ?? false;
    const pct = split ? Number($<HTMLInputElement>('sp-pct').value) : 0;
    // Not "All Tiffany's" here: a transfer item's entry is my own plan.
    if (split && !validSplitPercent(pct, false)) {
      $('f-err').textContent = DONE_PERCENT_HINT;
      return;
    }
    const partner = d.partner.linked?.name ?? 'your partner';
    let added = '';
    let splitNote = '';
    return busyMutate(
      b,
      async () => {
        added = (await (cat ? api.markCategoryDone(cat, { amountCents, date }) : api.markDone(id!, { amountCents, date }))).transaction.id;
        // The entry is saved either way; a refused split leaves it in full, and says why.
        if (split) {
          try {
            // No note: a Done entry has no "What was this for?" (Done never writes one).
            await api.sendSplit(added, pct, '');
            splitNote = ` ${SPLIT_SENT(partner)}`;
          } catch (e) {
            splitNote = ` ${SPLIT_NOT_SENT('Saved', splitRefusal(e))}`;
          }
        }
      },
      {
        before: closeSheet,
        success: () => `${name}: ${money(amountCents, 2)} moved.${splitNote}`,
        // Undo deletes the entry; a waiting split on it is cancelled with it.
        undo: () => api.deleteTransaction(added),
      },
    );
  },
  'item-not-done': (b) => {
    const { id, cat } = b.dataset;
    // Removes the latest entry under the same rules as Delete: a waiting split on it is cancelled
    // (and said); an accepted one refuses, with the reason under the button. Undo brings the entry
    // back, and the split with it when it still can, saying so either way.
    let removed = '';
    let what = 'Removed the latest entry';
    return busyMutate(
      b,
      async () => {
        const r = await (cat ? api.markCategoryNotDone(cat, viewedPastMonth()) : api.markNotDone(id!, viewedPastMonth()));
        removed = r.removedTransactionId;
        what = r.splitCancelled ? `${removedText(r.removed)}. ${r.splitCancelled}` : removedText(r.removed);
      },
      {
        success: () => what,
        undo: async () => {
          const r = await api.restoreTransaction(removed);
          const said = r.splitRestored ?? r.splitNotRestored;
          if (said) toast(`Restored. ${said}`);
        },
      },
    );
  },
  'income-add': () => openIncomeSheet(null),
  'income-edit': (b) => openIncomeSheet(store.data!.income.find((l) => l.id === b.dataset.id) ?? null),
  'income-save': async (b) => {
    const err = (msg: string) => ($('f-err').textContent = msg);
    const name = $<HTMLInputElement>('n-name').value.trim();
    const amountCents = parseCents($<HTMLInputElement>('n-amt').value);
    const schedule = $<HTMLSelectElement>('n-schedule').value as IncomeFields['schedule'];
    if (!name) return err('Give it a name.');
    if (!(amountCents > 0)) return err('Enter the amount of one paycheck, after taxes.');
    const day1 = Number($<HTMLSelectElement>('n-day1').value);
    const day2 = Number($<HTMLSelectElement>('n-day2').value);
    if (schedule === 'twice_monthly' && day1 === day2) return err('Pick two different days.');
    const anchor = $<HTMLInputElement>('n-anchor').value || null;
    if ((schedule === 'biweekly' || schedule === 'weekly') && !anchor) return err('Pick one of its pay dates.');
    const fields: IncomeFields = {
      name,
      amountCents,
      schedule,
      day1: schedule === 'monthly' || schedule === 'twice_monthly' ? day1 : null,
      day2: schedule === 'twice_monthly' ? day2 : null,
      anchor: schedule === 'biweekly' || schedule === 'weekly' ? anchor : null,
      startsMonth: $<HTMLInputElement>('n-starts').value || store.data!.month.key,
    };
    const id = $<HTMLInputElement>('n-id').value;
    await busyMutate(b, () => (id ? api.updateIncome(id, fields) : api.addIncome(fields)), { before: closeSheet, success: id ? `Saved ${name}` : `Added ${name}` });
  },
  'income-archive': (b) => {
    const line = store.data!.income.find((l) => l.id === b.dataset.id);
    if (!line) return;
    const archived = b.dataset.archived !== 'true';
    closeSheet();
    return mutate(() => api.updateIncome(line.id, { ...incomeFields(line, store.data!.month.key), archived }), archived ? 'Archived. It still counts in earlier months.' : 'Restored');
  },
  'income-details': (b) => {
    const line = store.data!.income.find((l) => l.id === b.dataset.id);
    if (!line) return;
    ui.sheet = { id: null, cat: null, armed: false };
    $('sheet').innerHTML = vDetailsSheet(line);
    $('sheet').classList.add('on', 'full');
    $('scrim').classList.add('on');
    setTimeout(() => $('dt-text')?.focus(), 280);
  },
  'income-details-save': (b) => {
    const id = $<HTMLInputElement>('dt-id').value;
    const text = $<HTMLTextAreaElement>('dt-text').value;
    return busyMutate(b, () => api.setIncomeDetails(id, text.trim() ? text : null), { before: closeSheet, success: text.trim() ? 'Details saved' : 'Details cleared' });
  },
  'income-delete': (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again to delete';
      return;
    }
    closeSheet();
    return mutate(() => api.deleteIncome(b.dataset.id!), 'Deleted from every month');
  },
  'income-snooze': () => mutate(() => api.dismissIncomeCard(), 'Hidden for 3 days. Add income any time on the Budget tab.'),
  'card-filter': (b) => {
    ui.card = b.dataset.card!;
    render();
  },
  'card-rename': (b) => {
    const card = store.data!.cards.find((k) => k.id === b.dataset.id);
    if (!card) return;
    ui.sheet = { id: null, cat: null, armed: false };
    $('sheet').innerHTML = `<div class="grab"></div><h2>Rename card <small>...${esc(card.last4)}</small></h2>
  <input type="hidden" id="c-id" value="${esc(card.id)}">
  <label class="fld"><span>Name</span><input id="c-name" type="text" maxlength="40" placeholder="${esc(`${card.bank ?? 'Card'} ...${card.last4}`)}" value="${esc(card.nickname ?? '')}"></label>
  <p class="hint">Leave it empty to show the bank and last four digits.</p>
  <div class="btns"><button class="btn primary" data-a="card-save">Save</button><button class="btn" data-a="close">Cancel</button></div>`;
    $('scrim').classList.add('on');
    $('sheet').classList.add('on');
  },
  'card-save': (b) => {
    const id = $<HTMLInputElement>('c-id').value;
    const nickname = $<HTMLInputElement>('c-name').value.trim() || null;
    return busyMutate(b, () => api.renameCard(id, nickname), { before: closeSheet, success: nickname ? `Card renamed to ${nickname}` : 'Card name cleared' });
  },
  'move-snooze': () => mutate(() => api.dismissMoveCard(), 'Hidden for 3 days. Transfers stay on their category screens.'),
  'cat-kind': (b) => {
    const input = b as HTMLInputElement;
    const kind = input.checked ? 'set_aside' : 'spending';
    return mutate(() => api.updateCategory(input.dataset.id!, { kind }), kind === 'set_aside' ? 'Now money set aside. It no longer counts as spending.' : 'Now spending again');
  },
  'item-month': (b) => {
    const m = Number(b.dataset.m);
    const yearly = $<HTMLSelectElement>('i-cadence').value === 'yearly';
    ui.itemMonths = ui.itemMonths.includes(m) ? ui.itemMonths.filter((x) => x !== m) : yearly ? [m] : [...ui.itemMonths, m];
    document.querySelectorAll<HTMLElement>('#sheet [data-a="item-month"]').forEach((x) => x.setAttribute('aria-pressed', String(ui.itemMonths.includes(Number(x.dataset.m)))));
    syncYearlyFields('item-month');
  },
  'item-end-toggle': (b) => {
    b.hidden = true;
    $('i-ends-f').hidden = false;
    $('i-ends').focus();
  },
  'item-save': async (b) => {
    const name = $<HTMLInputElement>('i-name').value.trim();
    const amountCents = parseCents($<HTMLInputElement>('i-amt').value);
    const cadence = $<HTMLSelectElement>('i-cadence').value as ItemFields['cadence'];
    const err = (msg: string) => ($('f-err').textContent = msg);
    if (!name) return err('Give it a name.');
    if (!(amountCents >= 0)) return err('Enter an amount, or 0.');

    if (cadence === 'every_n_months' && !ui.itemMonths.length) return err('Pick the months it is paid in.');
    const day = $<HTMLSelectElement>('i-day').value;
    // Transfers have no "last month's amount" and no auto-log.
    const transfer = $<HTMLInputElement>('i-kind').value === 'transfer';
    const fields: ItemFields = {
      name,
      amountCents,
      expectedMode: !transfer && $<HTMLInputElement>('i-last').checked ? 'last_month' : 'fixed',
      dueDay: day ? Number(day) : null,
      cadence,
      months: cadence === 'monthly' ? [] : ui.itemMonths,
      note: $<HTMLInputElement>('i-note').value.trim() || null,
      autoLog: !transfer && $<HTMLInputElement>('i-auto').checked,
    };
    // Yearly: the renewal date comes from the last charge (12 months on), else "When does it
    // renew?"; then whether it renews, how it counts, and a typed end month if any.
    if (!transfer && cadence === 'yearly') {
      const y = readYearly();
      const input = $<HTMLInputElement>('i-renewson');
      const schedule = yearlySchedule({ ...y, shownRenewsOn: input.dataset.orig || null, anyDay: input.dataset.any === '1' });
      if (!schedule) return err('Add when it was last charged, or when it renews.');
      Object.assign(fields, {
        expectedMode: 'fixed',
        ...schedule,
        spread: y.spread,
        autoLog: y.spread ? false : fields.autoLog,
        lastPaidOn: y.lastPaidOn,
        renews: y.renews,
        endsMonth: y.endsMonth,
      });
    } else if (cadence === 'yearly' && ui.itemMonths.length !== 1) return err('Pick the month it is paid in.');
    const id = $<HTMLInputElement>('i-id').value;
    const categoryId = $<HTMLInputElement>('i-cat').value;
    const from = $<HTMLInputElement>('i-from').value || undefined;
    await busyMutate(b, () => (id ? api.updateItem(id, fields) : api.addItem({ ...fields, categoryId, kind: transfer ? 'transfer' : 'expense', fromTransactionId: from })), {
      before: closeSheet,
      success: id ? `Saved ${name}` : `Added ${name}`,
    });
  },
  // A yearly item's renewal cards (Insights).
  renewal: (b) => {
    const id = b.dataset.id!;
    const answer = b.dataset.answer as RenewalAnswer;
    const item = store.data!.items.find((x) => x.id === id);
    if (!item) return;
    if (answer === 'amount') return openItemSheet(item, item.categoryId);
    if (answer === 'renewed') {
      ui.sheet = { id: null, cat: item.categoryId, armed: false };
      $('sheet').innerHTML = vRenewedSheet(store.data!, item);
      $('scrim').classList.add('on');
      $('sheet').classList.add('on');
      return;
    }
    const done: Record<string, string> = {
      keep: `Keeping ${item.name}`,
      cancel: `${item.name} will not renew`,
      ended: `${item.name} ended`,
      later: 'Hush will ask again in a week',
      seen: 'Got it',
    };
    return mutate(() => api.renewal(id, { answer: answer as 'keep' | 'cancel' | 'ended' | 'later' | 'seen', on: b.dataset.on }), done[answer]);
  },
  'renewed-save': (b) => {
    const id = $<HTMLInputElement>('d-id').value;
    const amountCents = parseCents($<HTMLInputElement>('d-amt').value);
    if (!(amountCents > 0)) {
      $('f-err').textContent = 'Enter an amount above $0.';
      return;
    }
    const date = $<HTMLInputElement>('d-date').value;
    if (!date) {
      $('f-err').textContent = 'Pick the day it renewed.';
      return;
    }
    const name = store.data!.items.find((x) => x.id === id)?.name ?? 'It';
    let added = '';
    return busyMutate(b, async () => (added = (await api.renewed(id, { amountCents, date })).transaction.id), {
      before: closeSheet,
      success: `${name} renewed`,
      undo: () => api.deleteTransaction(added),
    });
  },
  'item-archive': (b) => {
    const archived = b.dataset.archived !== 'true';
    closeSheet();
    return mutate(() => api.updateItem(b.dataset.id!, { archived }), archived ? 'Archived. It no longer counts.' : 'Restored');
  },
  'item-delete': (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again to delete';
      return;
    }
    closeSheet();
    return mutate(() => api.deleteItem(b.dataset.id!), 'Deleted. Its purchases stay, under Other.');
  },
  save: (b) => saveSheet(b),
  'reset-name': () => {
    const id = ui.sheet?.id;
    if (!id) return;
    closeSheet();
    return mutate(() => api.resetName(id), 'Back to the original name');
  },
  del: (b) => {
    const s = ui.sheet!;
    if (!s.armed) {
      s.armed = true;
      b.textContent = 'Tap again to delete';
      return;
    }
    const t = store.data!.transactions.find((x) => x.id === s.id)!;
    closeSheet();
    const del = deletion(t, (split) => (split ? `Deleted ${t.merchant}. ${split}` : 'Deleted ' + t.merchant));
    return mutate(del.work, del.success, del.undo);
  },
  // Delete on a New purchases row or its Edit sheet: the same delete as Activity's (deletion()),
  // no confirm; the toast's Undo brings it back. An accepted split refuses it under the button.
  'new-del': (b) => {
    const id = b.dataset.id!;
    const t = store.data!.transactions.find((x) => x.id === id);
    if (!t) return;
    const fromSheet = b.dataset.from === 'sheet';
    const del = deletion(t, (split) => (split ? `Deleted. ${split}` : 'Deleted.'));
    return busyMutate(b, del.work, {
      before: () => {
        clearRow(id);
        if (fromSheet) closeSheet();
      },
      fade: [id],
      success: del.success,
      undo: del.undo,
    });
  },
  // Splits with the linked partner (views/splits.ts).
  'split-open': () => {
    const t = store.data!.transactions.find((x) => x.id === ui.sheet?.id);
    if (!t) return;
    // The edit sheet's "What was this for?" as it stands (typed but not saved yet is kept).
    const field = document.getElementById('f-note') as HTMLInputElement | null;
    $('sheet').innerHTML = vSplitSheet(store.data!, t, field ? cleanNote(field.value) : t.note);
    void loadNoteChips('f-note', t.bankText);
  },
  // The sheet's "Paid from savings": open the funds, or pick one ('' none). Read on save.
  'fund-open': () => redrawSheetFund($<HTMLInputElement>('f-fund').value),
  'fund-pick': (b) => redrawSheetFund(b.dataset.fund ?? ''),
  // The sheet's "Add tip": open the fields, a quick pick (percent of the amount), or "No tip".
  'tip-open': () => {
    $('f-tip-opener').hidden = true;
    $('f-tip-fields').hidden = false;
    syncSheetTip('tip');
    $<HTMLInputElement>('f-tip').focus();
  },
  'tip-pct': (b) => {
    const charge = parseCents($<HTMLInputElement>('f-amt').value);
    $<HTMLInputElement>('f-tip').value = charge > 0 ? (tipFor(charge, Number(b.dataset.pct)) / 100).toFixed(2) : '';
    syncSheetTip('tip');
  },
  'tip-clear': () => {
    $<HTMLInputElement>('f-tip').value = '';
    $<HTMLInputElement>('f-total').value = '';
    $('f-tip-fields').hidden = true;
    $('f-tip-opener').hidden = false;
    syncSheetTip('tip');
  },
  'split-pct': (b) => {
    const input = $<HTMLInputElement>('sp-pct');
    input.value = b.dataset.pct!;
    if (document.getElementById('d-split')) return syncDoneSplitAmount();
    updateSplitShares();
  },
  'split-send': async (b) => {
    const id = ui.sheet?.id;
    const pct = Number($<HTMLInputElement>('sp-pct').value);
    if (!id || !validSplitPercent(pct)) {
      $('sp-err').textContent = SPLIT_PERCENT_HINT;
      return;
    }
    // The purchase's "What was this for?", shown with "Tiffany will see this.": saved on the
    // purchase first when it changed, so the request carries the purchase's note ('' none).
    const typed = cleanNote($<HTMLInputElement>('f-note').value);
    const saved = store.data!.transactions.find((x) => x.id === id)?.note ?? null;
    const note = splitNoteOf(typed);
    const partner = store.data!.partner.linked?.name ?? 'your partner';
    await busyMutate(
      b,
      async () => {
        if (typed !== saved) await api.updateTransaction(id, { note: typed ?? '' });
        await api.sendSplit(id, pct, note);
      },
      { before: closeSheet, success: `Sent to ${partner}. Your budget counts your share for now.` },
    );
  },
  'split-cancel': (b) => {
    closeSheet();
    return mutate(() => api.cancelSplit(b.dataset.id!), 'Request cancelled. The full amount is back in your budget.');
  },
  'split-notice-ok': (b) => busyMutate(b, () => api.dismissSplitNotice(b.dataset.id!)),
  'split-remove': (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again: the full amount counts again';
      return;
    }
    closeSheet();
    return mutate(() => api.removeSplit(b.dataset.id!), 'Split removed from this purchase. The full amount counts in your budget.');
  },
  // Months (views/month.ts): the list, the arrows and "Back to November" (data-month '': the active month).
  'month-list': () => {
    $('sheet').innerHTML = vMonthSheet(store.data!);
    $('scrim').classList.add('on');
    $('sheet').classList.add('on');
  },
  'month-go': async (b) => {
    const d = store.data!;
    const m = b.dataset.month || null;
    if (document.getElementById('sheet')?.classList.contains('on')) closeSheet();
    go({ to: 'month', month: m && m !== d.month.activeKey ? m : null }, { draw: false });
    await refresh();
    window.scrollTo(0, 0);
  },
  // "Start your November budget": the next month becomes the active one, and the Overview lands on it.
  'month-start': (b) => {
    const m = b.dataset.month!;
    return busyMutate(b, () => api.startMonth(m), {
      before: () => go({ to: 'month', month: null }, { draw: false }),
      success: MONTH_STARTED(m),
    });
  },
  'split-accept-open': (b) => {
    const r = store.data!.splitRequests.find((x) => x.id === b.dataset.id);
    if (!r) return;
    accepting = acceptStart(r);
    ui.sheet = { id: null, cat: null, armed: false };
    renderAcceptSheet();
    $('scrim').classList.add('on');
    $('sheet').classList.add('on');
  },
  'split-accept-pick': (b) => {
    if (!accepting) return;
    // The fold stays where the person left it; untouched, it follows the pick.
    accepting = { ...accepting, cat: b.dataset.cat!, item: null, asideOpen: accepting.asideOpen ?? (asideStartsOpen(store.data!, accepting.cat) || undefined) };
    renderAcceptSheet();
  },
  // "Or money set aside": open or fold the set-aside pills (a pick inside keeps it as it is).
  'split-accept-aside': () => {
    if (!accepting) return;
    accepting = { ...accepting, asideOpen: !asideStartsOpen(store.data!, accepting.cat, accepting.asideOpen) };
    renderAcceptSheet();
  },
  'split-accept-item': (b) => {
    if (!accepting) return;
    accepting = { ...accepting, item: b.dataset.item || null };
    renderAcceptSheet();
  },
  'split-accept': async (b) => {
    const a = accepting;
    if (!a?.cat) return;
    // My share's "What was this for?" exactly as the field shows it (my household only).
    await busyMutate(b, () => api.acceptSplit(a.id, a.cat!, a.item, a.note.trim()), {
      before: () => {
        accepting = null;
        closeSheet();
      },
      success: `Accepted. Your share is in ${catName(a.cat)}.`,
    });
  },
  'split-decline': (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again to decline';
      return;
    }
    return busyMutate(b, () => api.declineSplit(b.dataset.id!), { success: 'Declined. Nothing was added to your budget.' });
  },
  // The New purchases card (views/newcard.ts, views/newsheet.ts). Rows are collapsed: Confirm and
  // Edit. Edit opens the row's sheet; what is picked there stays on the row until Confirm.
  'new-edit': (b) => openNewSheet(b.dataset.id!),
  'new-sheet-done': () => closeSheet(),
  'new-row': (b) => {
    const s = ui.newSheet;
    const row = b.dataset.row as NewSheetRow;
    if (!s) return;
    if (s.open.has(row)) s.open.delete(row);
    else s.open.add(row);
    refreshNew();
  },
  'new-pick': (b) => {
    const id = b.dataset.id!;
    if (ui.picks.get(id) !== b.dataset.cat) ui.newItems.delete(id); // an item belongs to its category
    ui.picks.set(id, b.dataset.cat!);
    refreshNew();
  },
  'new-item-pick': (b) => {
    ui.newItems.set(b.dataset.id!, b.dataset.item || null);
    refreshNew();
  },
  // Confirm (or "Confirm and split") on a row or in its sheet: the category, item, tip and fund in
  // one PATCH, then the split request (confirmThenSplit()); a refused split leaves it filed in full.
  'new-confirm': (b) => confirmRow(b, 'new-confirm'),
  'new-ok': (b) => confirmRow(b, 'new-ok'),
  // "Confirm all N": the categories (and items) picked; never a split, tip or fund (named in the toast).
  'new-confirm-all': (b) => confirmAllRows(b, 'new-confirm-all'),
  'new-ok-all': (b) => confirmAllRows(b, 'new-ok-all'),
  // The tip row: a quick pick, or "No tip" (none, the row closes).
  'new-tip-open': (b) => {
    ensureTip(b.dataset.id!);
    refreshNew();
  },
  'new-tip-pct': (b) => {
    const t = store.data!.transactions.find((x) => x.id === b.dataset.id);
    const tip = t && ensureTip(t.id);
    if (!t || !tip) return;
    tip.tipCents = tipFor(t.amountCents, Number(b.dataset.pct));
    tip.below = false;
    refreshNew();
  },
  'new-tip-clear': (b) => {
    const tip = ensureTip(b.dataset.id!);
    if (tip) Object.assign(tip, { tipCents: null, below: false, off: true });
    refreshNew();
  },
  // Paid from savings: open the funds, or pick one ('' takes it off). Saved with the confirm; the
  // row's "Paid from Medical again?" is the same pick, one tap, never made by itself.
  'new-fund-open': (b) => {
    ui.newFunds.set(b.dataset.id!, '');
    refreshNew();
  },
  'new-fund-pick': (b) => {
    ui.newFunds.set(b.dataset.id!, b.dataset.fund ?? '');
    // Picking a fund (or "Paid from Medical again?") switches "Paid from savings" on.
    ui.newFundOn.set(b.dataset.id!, !!b.dataset.fund);
    refreshNew();
  },
  'new-split-pct': (b) => {
    const s = ui.newSplits.get(b.dataset.id!);
    if (!s) return;
    s.pct = Number(b.dataset.pct);
    refreshNew();
  },
  'see-new': () => go({ to: 'activity', filter: 'new' }),
  raise: (b) => {
    const k = store.data!.categories.find((x) => x.id === b.dataset.cat)!;
    const old = k.budgetCents;
    const next = Number(b.dataset.amt);
    return mutate(
      () => api.updateCategory(k.id, { budgetCents: next }),
      `${k.name} budget is now ${money(next)}`,
      () => api.updateCategory(k.id, { budgetCents: old }),
    );
  },
  dismiss: (b) => mutate(() => api.dismissInsight(store.data!.month.key, b.dataset.id!)),
  'confirm-candidate': async (b) => {
    const id = b.dataset.id!;
    try {
      await api.confirmCandidate(id, b.dataset.cat);
    } catch (e) {
      // Its merchant has no category yet: ask once, with the category picker.
      if (e instanceof ApiError && errorData.get(e)?.needsCategory) return openCandidateCategory(id);
      return toast(errorText(e));
    }
    closeSheet();
    await refresh();
    toast('Added as a monthly item. Hush logs it on its due day if no alert arrives.');
  },
  'decline-candidate': (b) => mutate(() => api.declineCandidate(b.dataset.id!), 'Got it. Hush will not ask again.'),
  mode: (b) => {
    const mode = b.dataset.mode as 'ask' | 'quiet';
    if (store.data!.user.mode === mode) return;
    return mutate(() => api.setMode(mode), mode === 'ask' ? 'Hush will ask about new purchases' : 'Hush will stay quiet');
  },
  // "Add category" inside a Budget group: it goes in that group and takes its kind.
  addcat: (b) => {
    const group = store.data!.groups.find((g) => g.id === b.dataset.group);
    if (!group) return;
    const name = $<HTMLInputElement>(`newcat-${group.id}`).value.trim();
    const amount = document.getElementById(`newbud-${group.id}`) as HTMLInputElement | null;
    const cents = amount ? parseCents(amount.value) : 0;
    if (!name) return toast('Enter a category name first');
    return mutate(
      () => api.addCategory(name, cents > 0 ? cents : 0, group.kind, group.id),
      group.kind === 'set_aside' ? `Added ${name}. Tap Done each month once the money has moved.` : `Added ${name}`,
    );
  },
  'group-toggle': (b) => {
    const g = store.data!.groups.find((x) => x.id === b.dataset.id);
    if (!g) return;
    g.collapsed = !g.collapsed; // shown at once, remembered for the household
    render();
    void api.updateGroup(g.id, { collapsed: g.collapsed }).catch((e) => toast(errorText(e)));
  },
  'group-add': () => openGroupSheet(null),
  'group-options': (b) => openGroupSheet(store.data!.groups.find((g) => g.id === b.dataset.id) ?? null),
  'group-save': async (b) => {
    const err = (msg: string) => ($('f-err').textContent = msg);
    const name = $<HTMLInputElement>('g-name').value.trim();
    if (!name) return err('Give the group a name.');
    const pct = (id: string) => {
      const v = $<HTMLInputElement>(id).value.trim().replace('%', '');
      return v === '' ? null : Number(v);
    };
    const min = pct('g-min');
    const max = pct('g-max');
    if ([min, max].some((v) => v !== null && !(Number.isInteger(v) && v >= 0 && v <= 100))) return err('Targets are whole percents from 0 to 100.');
    const kind = $<HTMLSelectElement>('g-kind').value as 'spending' | 'set_aside';
    const id = $<HTMLInputElement>('g-id').value;
    // A refusal (e.g. a kind its categories' items do not allow) shows under Save.
    await busyMutate(b, () => (id ? api.updateGroup(id, { name, kind, targetMinPct: min, targetMaxPct: max }) : api.addGroup({ name, kind, targetMinPct: min, targetMaxPct: max })), {
      before: closeSheet,
      success: id ? `Saved ${name}` : `Added ${name}`,
    });
  },
  'group-delete': async (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again to delete';
      return;
    }
    try {
      await api.deleteGroup(b.dataset.id!);
    } catch (e) {
      return ($('f-err').textContent = errorText(e));
    }
    closeSheet();
    await refresh();
    toast('Group deleted');
  },
  'cat-delete': async (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again to delete';
      return;
    }
    const k = store.data!.categories.find((x) => x.id === b.dataset.id);
    try {
      await api.deleteCategory(b.dataset.id!);
    } catch (e) {
      // Still used in an earlier month: say why, with the way to its Activity.
      const why = document.getElementById('cat-del-why');
      if (why) {
        why.hidden = false;
        why.textContent = errorText(e);
        b.outerHTML = `<button class="btn" data-a="filter" data-cat="${esc(b.dataset.id!)}">See its activity</button>`;
      }
      return;
    }
    if (ui.drawer) closeDrawer();
    if (ui.categoryId) go({ to: 'back' }, { draw: false });
    await refresh();
    toast(`Deleted ${k?.name ?? 'the category'}`);
  },
  copy: (b) => {
    const from = b.dataset.from ?? 'addr';
    const text = $(from).textContent ?? '';
    try {
      navigator.clipboard.writeText(text).then(
        () => toast(from === 'addr' ? 'Address copied' : `${b.dataset.what ?? 'Code'} copied`),
        () => toast('Copy failed. Select the address to copy it.'),
      );
    } catch {
      toast('Copy failed. Select the address to copy it.');
    }
  },
  undo: () => {
    const fn = ui.undo;
    ui.undo = null;
    $('toast').classList.remove('on');
    fn?.();
  },
  signup: async (b) => {
    const name = $<HTMLInputElement>('w-name').value.trim();
    const code = $<HTMLInputElement>('w-code').value.trim();
    if (!name) {
      $('w-err').textContent = 'Enter your name to start.';
      return;
    }
    b.setAttribute('disabled', '');
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const res = await api.signup(name, tz, code || undefined);
      go({ to: 'start' }, { draw: false });
      const start = () => {
        void refresh().then(() => toast('Set your monthly amounts to start tracking.'));
      };
      if (res.recoveryCode) showRecoveryCode(res.recoveryCode, 'signup', start);
      else start();
    } catch (e) {
      $('w-err').textContent = errorText(e);
      b.removeAttribute('disabled');
    }
  },
  signout: async () => {
    try {
      await api.signout();
    } catch {
      /* the token is dropped locally either way */
    }
    signedOut();
  },
  'signout-all': async (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again to sign out everywhere';
      return;
    }
    try {
      await api.signoutAll();
    } catch (e) {
      return toast(errorText(e));
    }
    signedOut();
    toast('Signed out on every device. Use your recovery code or email to come back.');
  },
  'partner-open': () => {
    ui.partner = { ...ui.partner, open: true };
    render();
  },
  'partner-invite-send': async (b) => {
    const email = $<HTMLInputElement>('partner-email').value.trim();
    const name = $<HTMLInputElement>('partner-name').value.trim();
    if (!email) return toast("Enter your partner's email");
    b.setAttribute('disabled', '');
    try {
      await api.sendPartnerInvite(email, name || undefined);
      ui.partner = { open: false, code: null, expiresAt: null };
      toast(`Invite sent${name ? ` to ${name}` : ''}. You are linked as soon as they join.`);
      await refresh();
    } catch (e) {
      toast(errorText(e));
      b.removeAttribute('disabled');
    }
  },
  'partner-invite-cancel': (b) => mutate(() => api.cancelPartnerInvite(b.dataset.id!), 'Invite cancelled. The link no longer works.'),
  'partner-invite': async (b) => {
    b.setAttribute('disabled', '');
    try {
      const r = await api.partnerInvite();
      ui.partner = { open: true, code: r.code, expiresAt: r.expiresAt };
      await refresh();
    } catch (e) {
      toast(errorText(e));
      b.removeAttribute('disabled');
    }
  },
  'partner-accept': async (b) => {
    const code = $<HTMLInputElement>('partner-code').value.trim();
    if (!code) return toast('Enter the code your partner made');
    b.setAttribute('disabled', '');
    try {
      const { partner } = await api.partnerAccept(code);
      ui.partner = { open: false, code: null, expiresAt: null };
      toast(`Linked with ${partner.linked?.name ?? 'your partner'}`);
      await refresh();
    } catch (e) {
      toast(errorText(e));
      b.removeAttribute('disabled');
    }
  },
  'partner-unlink': async (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again to unlink';
      return;
    }
    try {
      await api.partnerUnlink();
      ui.partner = { open: false, code: null, expiresAt: null };
      toast('Unlinked. Your own budget has not changed.');
      await refresh();
    } catch (e) {
      toast(errorText(e));
    }
  },
  'set-email': async (b) => {
    const email = $<HTMLInputElement>('signin-email').value.trim();
    if (!email) return toast('Enter your email first');
    b.setAttribute('disabled', '');
    try {
      toast((await api.setSigninEmail(email)).message);
    } catch (e) {
      toast(errorText(e));
    }
    b.removeAttribute('disabled');
  },
  'welcome-mode': (b) => {
    const modes: WelcomeMode[] = ['start', 'invite-help', 'code', 'signin', 'recover'];
    welcomeMode = modes.find((m) => m === b.dataset.mode) ?? 'start';
    renderWelcome();
  },
  // An emailed partner invite: sign up with the email it was sent to; linked on the way in.
  'invite-signup': async (b) => {
    const name = $<HTMLInputElement>('w-name').value.trim();
    const email = $<HTMLInputElement>('w-email').value.trim();
    if (!name || !email) {
      $('w-err').textContent = !name ? 'Enter your first name.' : 'Enter the email address the invite was sent to.';
      return;
    }
    if (!inviteToken) return;
    b.setAttribute('disabled', '');
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const res = await api.inviteSignup(inviteToken, email, name, tz);
      const from = inviteInfo?.inviterName ?? 'your partner';
      inviteToken = null;
      inviteInfo = null;
      go({ to: 'start' }, { draw: false });
      const start = () => void refresh().then(() => toast(`You are linked with ${from}. Set your monthly amounts to start tracking.`));
      if (res.recoveryCode) showRecoveryCode(res.recoveryCode, 'signup', start);
      else start();
    } catch (e) {
      // That email has an account already: sign in with it, then open the invite link again.
      if (e instanceof ApiError && e.status === 409) {
        welcomeMode = 'signin';
        welcomeEmail = email;
        renderWelcome();
      }
      $('w-err').textContent = errorText(e);
      b.removeAttribute('disabled');
    }
  },
  'partner-invite-use': async (b) => {
    if (!inviteToken) return closeSheet();
    b.setAttribute('disabled', '');
    const token = inviteToken;
    inviteToken = null;
    closeSheet();
    await mutate(async () => {
      const { partner } = await api.useInvite(token);
      inviteInfo = null;
      if (!partner.linked) throw new Error('Could not link. Ask for a new invite.');
    }, () => `Linked with ${store.data?.partner.linked?.name ?? 'your partner'}`);
  },
  recover: async (b) => {
    const code = $<HTMLInputElement>('w-rcode').value.trim();
    if (!code) {
      $('w-err').textContent = 'Enter your recovery code.';
      return;
    }
    b.setAttribute('disabled', '');
    try {
      const res = await api.recover(code);
      go({ to: 'start' }, { draw: false });
      showRecoveryCode(res.recoveryCode!, 'recovered', () => void refresh());
    } catch (e) {
      $('w-err').textContent = errorText(e);
      b.removeAttribute('disabled');
    }
  },
  'recovery-saved': (b) => {
    const done = $('app').querySelector<HTMLButtonElement>('[data-a="recovery-done"]')!;
    done.disabled = !(b as HTMLInputElement).checked;
  },
  'recovery-done': () => {
    const next = recoveryNext;
    recoveryNext = null;
    next?.();
  },
  'recovery-new': async (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again: the old code stops working';
      return;
    }
    try {
      const { recoveryCode } = await api.newRecoveryCode();
      showRecoveryCode(recoveryCode, 'new', () => void refresh());
    } catch (e) {
      toast(errorText(e));
    }
  },
  'magic-link': async (b) => {
    const email = $<HTMLInputElement>('w-email').value.trim();
    if (!email) {
      $('w-err').textContent = 'Enter your email.';
      return;
    }
    b.setAttribute('disabled', '');
    try {
      $('w-err').textContent = '';
      toast((await api.startMagicLink(email)).message);
    } catch (e) {
      $('w-err').textContent = errorText(e);
    }
    b.removeAttribute('disabled');
  },
  'gmail-filter': async () => {
    try {
      saveBlob(await api.gmailFilter(), 'hush-gmail-filter.xml');
    } catch (e) {
      toast(errorText(e));
    }
  },
  export: async (b) => {
    const kind = b.dataset.kind === 'categories' ? 'categories' : 'transactions';
    const month = store.data!.month.key;
    try {
      saveBlob(await api.exportCsv(kind, month), `hush-${kind}-${month}.csv`);
    } catch (e) {
      toast(errorText(e));
    }
  },
  'export-all': async () => {
    try {
      saveBlob(await api.exportAll(), `hush-data-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (e) {
      toast(errorText(e));
    }
  },
  'delete-account': () => {
    ui.sheet = { id: null, cat: null, armed: false };
    $('sheet').innerHTML = vDeleteAccountSheet(store.data!);
    $('scrim').classList.add('on');
    $('sheet').classList.add('on');
    setTimeout(() => $('del-confirm')?.focus(), 280);
  },
  'delete-account-confirm': async (b) => {
    const typed = $<HTMLInputElement>('del-confirm').value.trim().toLowerCase();
    if (typed !== 'delete') return;
    b.setAttribute('disabled', '');
    try {
      await api.deleteAccount(typed);
    } catch (e) {
      $('del-err').textContent = errorText(e);
      b.removeAttribute('disabled');
      return;
    }
    closeSheet();
    signedOut();
    toast('Your account and data are deleted.');
  },
  'paste-open': () => openPasteSheet(),
  'paste-read': async (b) => {
    const text = $<HTMLTextAreaElement>('p-text').value.trim();
    if (!text) {
      $('f-err').textContent = 'Paste the alert first.';
      return;
    }
    b.setAttribute('disabled', '');
    b.textContent = 'Reading';
    try {
      pastePreview = await api.pastePreview(text);
      $('sheet').innerHTML = vPasteSheet(pastePreview);
    } catch (e) {
      const amount = e instanceof ApiError ? errorData.get(e)?.amountCents : undefined;
      $('f-err').textContent =
        errorText(e) + (typeof amount === 'number' ? ` It mentions ${money(amount, 2)}; you can add it with the + button.` : '');
      b.removeAttribute('disabled');
      b.textContent = 'Read it';
    }
  },
  'paste-add': async () => {
    if (!pastePreview) return;
    const id = pastePreview.previewId;
    closeSheet();
    pastePreview = null;
    try {
      const { result } = await api.pasteConfirm(id);
      await refresh();
      toast(
        result.status === 'merged'
          ? 'Linked with a purchase you already added'
          : result.status === 'pending-merge'
            ? 'This looks like something you added. Check Insights.'
            : result.status === 'duplicate'
              ? 'That alert is already in your budget'
              : result.categoryId
                ? `Added and filed under ${catName(result.categoryId)}`
                : 'Added. Pick a category for it in Review.',
      );
    } catch (e) {
      toast(errorText(e));
    }
  },
  'sample-load': async (b) => {
    b.setAttribute('disabled', '');
    try {
      await api.loadSample();
      go({ to: 'start' }, { draw: false });
      await refresh();
      window.scrollTo(0, 0);
      toast('Showing sample data. Leave it from Settings.');
    } catch (e) {
      toast(errorText(e));
      b.removeAttribute('disabled');
    }
  },
  'sample-leave': async () => {
    try {
      await api.leaveSample();
    } catch {
      /* it expires on its own */
    }
    await backToOwnAccount();
    toast('Back to your own budget');
  },
  'sender-allow': async (b) => {
    try {
      const { held } = await api.allowSender(b.dataset.sender!);
      await refresh();
      toast(
        !held || held.status === 'duplicate'
          ? 'Added. Alerts from it log themselves now.'
          : held.status === 'merged'
            ? 'Added, and linked with a purchase you already added'
            : held.status === 'pending-merge'
              ? 'Added. Its purchase looks like one you added; check Insights.'
              : held.categoryId
                ? `Added, and its purchase is filed under ${catName(held.categoryId)}`
                : 'Added, and its purchase is logged. Pick a category for it.',
      );
    } catch (e) {
      toast(errorText(e));
    }
  },
  'teach-accept': async (b) => {
    const id = b.dataset.id!;
    const val = (k: string) => $<HTMLInputElement>(`p-${k}-${id}`).value.trim();
    b.setAttribute('disabled', '');
    try {
      const { logged } = await api.acceptBankProposal(id, {
        name: val('name'),
        sender: val('sender'),
        subjectPattern: val('pattern'),
        alsoLog: $<HTMLInputElement>(`p-log-${id}`).checked,
      });
      await refresh();
      toast(logged ? 'Bank added to your filter, and the purchase is logged' : 'Bank added. The filter fields now include it.');
    } catch (e) {
      toast(errorText(e));
      b.removeAttribute('disabled');
    }
  },
  'teach-discard': (b) => mutate(() => api.discardBankProposal(b.dataset.id!), 'Forgotten. Nothing was kept.'),
  'bank-remove': async (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again';
      return;
    }
    await mutate(() => api.removeBank(b.dataset.id!), 'Removed. Update your Gmail filter with the new fields.');
  },
  'silence-dismiss': (b) => mutate(() => api.dismissSilence(b.dataset.key!), 'Dismissed until its next alert'),
  'my-address-add': async (b) => {
    const address = $<HTMLInputElement>('my-address').value.trim();
    if (!address) return toast('Enter your Gmail address');
    b.setAttribute('disabled', '');
    await mutate(() => api.addMyAddress(address), 'Added');
    b.removeAttribute('disabled');
  },
  'my-address-remove': (b) => mutate(() => api.removeMyAddress(b.dataset.address!), 'Removed'),
  'held-delete': (b) => mutate(() => api.deleteHeldMessage(b.dataset.id!), 'Deleted'),
  'sender-decline': (b) => mutate(() => api.declineSender(b.dataset.sender!), 'Got it. Hush will ignore that sender.'),
  'sender-remove': async (b) => {
    if (b.dataset.armed !== 'true') {
      b.dataset.armed = 'true';
      b.textContent = 'Tap again';
      return;
    }
    await mutate(() => api.removeSender(b.dataset.sender!), 'Removed. Mail from it is ignored now.');
  },
  'review-add': (b) => {
    const item = store.data!.reviewItems.find((r) => r.id === b.dataset.id);
    if (item) openSheet(null, item);
  },
  'review-dismiss': (b) => mutate(() => api.resolveReviewItem(b.dataset.id!), 'Dismissed'),
  // An item's price (data-id), or a "same amount every month" category's (data-cat).
  'confirm-price': (b) =>
    mutate(() => (b.dataset.cat ? api.answerCategoryPrice(b.dataset.cat, 'confirm') : api.answerPrice(b.dataset.id!, 'confirm')), 'Hush will expect the new price'),
  'reject-price': (b) =>
    mutate(() => (b.dataset.cat ? api.answerCategoryPrice(b.dataset.cat, 'reject') : api.answerPrice(b.dataset.id!, 'reject')), 'Kept the usual price'),
  merge: (b) => mutate(() => api.mergeTransaction(b.dataset.id!), 'Linked. It counts once.'),
  'keep-separate': (b) => mutate(() => api.keepSeparate(b.dataset.id!), 'Kept as two purchases'),
};

document.addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('[data-a]');
  if (!b) return;
  // A button already saving ignores more taps (busy.ts); the server also refuses repeats.
  if (isBusy(b)) return;
  void actions[b.dataset.a!]?.(b);
});

// Cents-first amounts (purchase and "Different amount" sheets only): see cents-input.ts.
document.addEventListener('beforeinput', onCentsBeforeInput);
document.addEventListener('input', onCentsInput);

// Typing an amount makes it the user's: no later pick replaces it.
document.addEventListener('input', (e) => {
  const t = e.target as HTMLElement;
  // The delete confirmation enables its button once "delete" is typed.
  if (t.id === 'del-confirm') {
    const ok = (t as HTMLInputElement).value.trim().toLowerCase() === 'delete';
    document.querySelector('[data-a="delete-account-confirm"]')?.toggleAttribute('disabled', !ok);
  }
  if (t.id === 'learn-search' && ui.learned) {
    ui.learned.search = (t as HTMLInputElement).value;
    filterLearned($('app'), ui.learned.search);
  }
  if (t.id === 'f-amt') delete (t as HTMLInputElement).dataset.autofill;
  // The sheet's tip: the tip, the final total and the amount keep each other in step.
  if (t.id === 'f-tip' || t.id === 'f-total' || t.id === 'f-amt') syncSheetTip(t.id === 'f-tip' ? 'tip' : t.id === 'f-total' ? 'total' : 'amount');
  if (t.id === 'f-mer') updateTipOpener();
  // An auto-logged charge's amount in its Edit sheet: kept until Confirm. The tip stays in dollars
  // (its final total follows), the split shares the new tipped total.
  if (t.dataset.newAmt) {
    const id = t.dataset.newAmt;
    const c = parseCents((t as HTMLInputElement).value);
    ui.newAmounts.set(id, Number.isFinite(c) && c > 0 ? c : null);
    const tip = ui.newTips.get(id);
    if (tip) {
      tip.below = false;
      syncRowTip(id, 'tip');
    } else {
      const row = store.data!.transactions.find((x) => x.id === id);
      const pct = document.querySelector<HTMLInputElement>(`[data-new-split-pct="${CSS.escape(id)}"]`);
      if (row && pct) {
        pct.dataset.full = String(rowCharge(row, choicesOf(newChoices(), id)) + (row.tipCents ?? 0));
        const shares = document.querySelector(`[data-new-split-shares="${CSS.escape(id)}"]`);
        if (shares) shares.textContent = sharesText(Number(pct.dataset.full), Number(pct.value), pct.dataset.partner!);
      }
    }
  }
  // A New purchases row's note: kept until Confirm (the sheet redraws from it).
  if (t.dataset.newNote) {
    const id = t.dataset.newNote;
    const value = (t as HTMLInputElement).value;
    ui.newNotes.set(id, value);
  }
  // The Accept sheet's "What was this for?": kept as typed, so a redraw (a pick) keeps it.
  if (t.dataset.acceptNote && accepting) accepting = { ...accepting, note: (t as HTMLInputElement).value };
  // "Tiffany will see this." follows the note as it is typed (the split sends it).
  if (t.id === 'f-note' || t.id === 'ns-note') syncNoteSees();
  if (t.id === 'act-search') {
    activitySearch = (t as HTMLInputElement).value;
    filterActivity($('app'), activitySearch);
  }
  // A New purchases row's tip: kept in ui.newTips, its total and split shares updated in place.
  const tipRow = t.dataset.newTip ?? t.dataset.newTotal;
  const rowTipState = tipRow ? ui.newTips.get(tipRow) : undefined;
  if (tipRow && rowTipState) {
    const value = centsOr((t as HTMLInputElement).value);
    if (t.dataset.newTip) {
      rowTipState.tipCents = value;
      rowTipState.below = false;
    } else {
      const charge = Number(t.dataset.charge);
      const tip = value ? tipFromTotal(charge, value) : 0;
      rowTipState.below = tip === null;
      if (tip !== null) rowTipState.tipCents = tip || null;
    }
    syncRowTip(tipRow, t.dataset.newTip ? 'tip' : 'total');
  }
  if (t.id === 'sp-pct' && document.getElementById('d-split')) syncDoneSplitAmount();
  else if (t.id === 'sp-pct' || (t.id === 'f-amt' && document.getElementById('f-split-fields')) || (t.id === 'd-amt' && document.getElementById('d-split'))) updateSplitShares();
  // A New purchases row's split: kept in ui.newSplits, the shares line updated in place.
  const row = t.dataset.newSplitPct;
  const s = row ? ui.newSplits.get(row) : undefined;
  if (row && s) {
    s.pct = Number((t as HTMLInputElement).value);
    const shares = document.querySelector(`[data-new-split-shares="${CSS.escape(row)}"]`);
    if (shares) shares.textContent = sharesText(Number((t as HTMLInputElement).dataset.full), s.pct, (t as HTMLInputElement).dataset.partner!);
    for (const chip of document.querySelectorAll<HTMLButtonElement>(`[data-a="new-split-pct"][data-id="${CSS.escape(row)}"]`))
      chip.setAttribute('aria-pressed', String(Number(chip.dataset.pct) === s.pct));
  }
});

document.addEventListener('change', (e) => {
  const target = e.target as HTMLElement;
  // Adding: the chips follow the merchant typed (once it is left).
  if (target.id === 'f-mer' && !ui.sheet?.id) void loadNoteChips('f-note', (target as HTMLInputElement).value);
  // The Splits screen: a ticked line goes into the next Settle up; unticked ones carry over.
  if (target.dataset.settleTick) {
    if ((target as HTMLInputElement).checked) ui.settleOff.delete(target.dataset.settleTick);
    else ui.settleOff.add(target.dataset.settleTick);
    return render();
  }
  // Settings, Notifications: a kind on or off (only that kind is sent).
  if (target.dataset.pushKind) {
    const kind = target.dataset.pushKind as PushKind;
    void pushAction(() => api.pushKinds({ [kind]: (target as HTMLInputElement).checked }));
    return;
  }
  // "Usually tipped" on a New purchases row: saved with its confirm.
  if (target.dataset.newTipped) {
    const tip = ui.newTips.get(target.dataset.newTipped);
    if (tip) tip.tipped = (target as HTMLInputElement).checked;
    return;
  }
  // The Edit sheet's Tip switch: on, the tip fields (no tip yet); off, no tip (cleared).
  if (target.dataset.newTipOn) {
    const id = target.dataset.newTipOn;
    const t = store.data!.transactions.find((x) => x.id === id);
    if (!t) return;
    ui.newTips.set(id, tipSwitched(t, ui.newTips.get(id), (target as HTMLInputElement).checked));
    return refreshNew();
  }
  // The "Paid from savings" switch: on shows the funds (none chosen yet); off clears the fund.
  if (target.dataset.newFundOn) {
    const id = target.dataset.newFundOn;
    const f = fundSwitched((target as HTMLInputElement).checked);
    ui.newFundOn.set(id, f.fundOn);
    ui.newFunds.set(id, f.fund);
    return refreshNew();
  }
  // "Split this bill" on a New purchases row: on shows the picker (50/50 to start).
  if (target.dataset.newSplit) {
    // Its note is the row's "What was this for?" (the redraw shows "Tiffany will see this." under it).
    if ((target as HTMLInputElement).checked) ui.newSplits.set(target.dataset.newSplit, startSplit());
    else ui.newSplits.delete(target.dataset.newSplit);
    return refreshNew();
  }
  // The Done sheet's "Split with Tiffany": the picker and the shares, the amount following it.
  if (target.id === 'd-split') {
    $('d-split-fields').hidden = !(target as HTMLInputElement).checked;
    return syncDoneSplitAmount();
  }
  // The add sheet's "Split this bill" shows the picker.
  if (target.id === 'f-split') {
    $('f-split-fields').hidden = !(target as HTMLInputElement).checked;
    syncNoteSees();
    return updateSplitShares();
  }
  // The edit sheet's correction options: the line says what saving will do.
  if (target.id === 'f-remember' || target.id === 'f-earlier') return updateLearn();
  // "What Hush has learned": a merchant's category.
  if (target.dataset.learnRule) {
    const k = store.data!.categories.find((x) => x.id === (target as HTMLSelectElement).value);
    return void learnedChange(() => api.setRuleCategory(target.dataset.learnRule!, (target as HTMLSelectElement).value), `From now on it files under ${k?.name ?? 'that category'}`);
  }
  // The item editor's yearly part (how it counts, Last paid) follows Repeats, the amount and Last paid.
  if (['i-cadence', 'i-amt', 'i-lastpaid', 'i-renewson', 'i-renews', 'i-ends'].includes(target.id) || (target as HTMLInputElement).name === 'i-count') syncYearlyFields(target.id);
  if ((target.id === 'i-last' || target.id === 'i-day') && document.getElementById('i-auto')) {
    // Auto-log needs a fixed amount and a due day. (Transfers have neither field.)
    const auto = $<HTMLInputElement>('i-auto');
    const can = !$<HTMLInputElement>('i-last').checked && $<HTMLSelectElement>('i-day').value !== '';
    auto.disabled = !can;
    if (!can) auto.checked = false;
    return;
  }
  if (target.id === 'i-cadence') {
    const cadence = (target as HTMLSelectElement).value;
    // Yearly items have their own questions (no month chips); every few months keeps the chips.
    $('i-months').hidden = cadence !== 'every_n_months' && !!document.getElementById('i-yearly');
    if (!document.getElementById('i-yearly')) $('i-months').hidden = cadence === 'monthly';
    if (cadence === 'yearly' && ui.itemMonths.length > 1) ui.itemMonths = ui.itemMonths.slice(0, 1);
    document.querySelectorAll<HTMLElement>('#sheet [data-a="item-month"]').forEach((x) => x.setAttribute('aria-pressed', String(ui.itemMonths.includes(Number(x.dataset.m)))));
    return;
  }
  if (target.id === 'n-schedule') {
    // The income sheet shows only the fields its schedule uses.
    const kind = (target as HTMLSelectElement).value;
    $('n-day1-f').hidden = !(kind === 'monthly' || kind === 'twice_monthly');
    $('n-day2-f').hidden = kind !== 'twice_monthly';
    $('n-anchor-f').hidden = !(kind === 'biweekly' || kind === 'weekly');
    return;
  }
  if (ui.drawer && target.id === 'dr-amt') {
    // Not redrawn (a tap on Save right after typing must still land): only the switch follows the amount.
    const zero = !(parseCents((target as HTMLInputElement).value) > 0);
    const same = document.getElementById('dr-same') as HTMLInputElement | null;
    if (same) {
      same.disabled = zero;
      if (zero) same.checked = false;
      $('dr-same-hint').textContent = zero ? SET_AMOUNT_FIRST : SAME_AMOUNT_HINT;
    }
    return;
  }
  if (ui.drawer && ['dr-group', 'dr-room', 'dr-same', 'dr-due'].includes(target.id)) {
    // The drawer: a set-aside group has no room for other spending; the total follows the room.
    readDraft();
    renderDrawer();
    return;
  }
  const carry = target.closest<HTMLInputElement>('[data-carry]');
  if (carry) {
    const cents = carry.value.trim() === '' ? 0 : parseCents(carry.value);
    if (!Number.isFinite(cents)) return toast('Enter an amount, or 0');
    void mutate(() => api.setCarryOver(carry.dataset.carry!, cents), 'Saved');
    return;
  }
  const rename = (e.target as HTMLElement).closest<HTMLInputElement>('[data-rename]');
  if (rename) {
    const name = rename.value.trim();
    if (!name) return toast('Enter a name for this bank');
    void mutate(() => api.renameBank(rename.dataset.rename!, name), 'Renamed');
    return;
  }
  const share = (e.target as HTMLElement).closest<HTMLInputElement>('[data-share]');
  if (share) {
    void mutate(() => api.setShareBanks(share.checked), share.checked ? 'Sharing on' : 'Sharing off');
    return;
  }
  const bank = (e.target as HTMLElement).closest<HTMLInputElement>('[data-bank]');
  if (bank) {
    const ids = [...document.querySelectorAll<HTMLInputElement>('[data-bank]')].filter((x) => x.checked).map((x) => x.dataset.bank!);
    void mutate(() => api.setBanks(ids));
    return;
  }
  const thr = (e.target as HTMLElement).closest<HTMLInputElement>('[data-threshold]');
  if (thr) {
    const raw = thr.value.trim();
    const cents = raw === '' ? null : parseCents(raw);
    if (cents !== null && (!Number.isFinite(cents) || cents < 0)) {
      toast('Enter an amount, 0 for every purchase, or leave it empty');
      return;
    }
    void mutate(() => api.setSenderThreshold(thr.dataset.threshold!, cents), cents === null ? 'Cleared' : cents === 0 ? 'Every purchase is covered' : 'Saved');
    return;
  }
  // Budget tab amounts save themselves (wireInlineFields).
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && ui.sheet) requestClose();
  // A focused grip: arrow keys move the category (within its group) or the group one place.
  const grip = (e.target as HTMLElement).closest?.<HTMLElement>('[data-grip]');
  if (grip && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
    e.preventDefault();
    const { grip: what, id } = grip.dataset as { grip: 'category' | 'group'; id: string };
    const dir = e.key === 'ArrowUp' ? 'up' : 'down';
    void mutate(() => (what === 'group' ? api.moveGroup(id, dir) : api.moveCategory(id, dir))).then((ok) => {
      if (ok) document.querySelector<HTMLElement>(`[data-grip="${what}"][data-id="${id}"]`)?.focus();
    });
  }
  if (e.key === 'Enter' && (e.target as HTMLElement).id === 'w-name') void actions.signup($('app').querySelector('[data-a="signup"]')!);
  if (e.key === 'Enter' && (e.target as HTMLElement).id === 'w-rcode') void actions.recover($('app').querySelector('[data-a="recover"]')!);
  if (e.key === 'Enter' && (e.target as HTMLElement).id === 'w-email') void actions['magic-link']($('app').querySelector('[data-a="magic-link"]')!);
});


/** Leave sample data (or recover from an expired sample) by switching back to the stashed token. */
/** After sample data: the server already switched the cookie back (leave), or does it now (expired). */
async function backToOwnAccount(expired = false) {
  store.data = null;
  go({ to: 'settings' }, { draw: false });
  if (expired) {
    try {
      await api.ownSession();
    } catch {
      return render(); // no own session left: the welcome screen
    }
  }
  await refresh();
}

handleUnauthorized(() => {
  ui.drawerReturn = null;
  closeSheet();
  if (store.data?.isSample) return void backToOwnAccount(true); // the sample household expired
  store.data = null;
  render();
});

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}

/**
 * An emailed partner invite (/#invite=ID.SECRET). Signed out: the invite form, name prefilled.
 * Signed in: "Link with Neil?". The token stays in memory only and leaves the address bar.
 */
async function openInviteLink(token: string) {
  history.replaceState(null, '', location.pathname);
  try {
    const info = await api.inviteCheck(token);
    inviteToken = token;
    inviteInfo = { inviterName: info.inviterName, name: info.name };
  } catch (e) {
    inviteToken = null;
    inviteInfo = null;
    await refresh();
    toast(errorText(e));
    return;
  }
  await refresh();
  if (store.data) {
    ui.sheet = { id: null, cat: null, armed: false };
    $('sheet').innerHTML = vUseInviteSheet(inviteInfo.inviterName, !!store.data.partner.linked);
    $('scrim').classList.add('on');
    $('sheet').classList.add('on');
  } else {
    welcomeMode = 'invite';
    renderWelcome();
  }
}

/**
 * Links from emails land on /#signin=TOKEN, /#verify-email=TOKEN or /#invite=ID.SECRET. A tapped
 * notification (shared/push.ts LINK, web/src/push-links.ts) lands on /#review, /#tx=ID,
 * /#accept=ID, /#splits or /#overview: read once, cleared, applied through go().
 */
async function handleLinkInUrl(): Promise<boolean> {
  const link = readPushLink(location.hash);
  if (link) {
    history.replaceState(null, '', location.pathname);
    await openPushLink(link);
    return true;
  }
  const invite = /^#invite=([\w.-]+)$/.exec(location.hash);
  if (invite) {
    await openInviteLink(invite[1]);
    return true;
  }
  const m = /^#(signin|verify-email)=([\w-]+)$/.exec(location.hash);
  if (!m) return false;
  history.replaceState(null, '', location.pathname); // the token is single use; drop it from the URL
  try {
    if (m[1] === 'signin') {
      const res = await api.completeMagicLink(m[2]);
      await refresh();
      toast(`Signed in as ${res.user.name}`);
    } else {
      const res = await api.verifyEmail(m[2]);
      if (store.data) await refresh();
      else renderWelcome();
      toast(`You can now sign in with ${res.email}`);
    }
  } catch (e) {
    if (store.data) await refresh();
    else renderWelcome();
    toast(errorText(e));
  }
  return true;
}

/** A tapped notification: the screen through go(), then the existing action for what it names. */
async function openPushLink(link: PushLink) {
  if (link.kind === 'review') {
    go({ to: 'activity', filter: 'new' }, { draw: false });
    await refresh();
    return;
  }
  // Everything else starts from Overview on the active month.
  go({ to: 'month', month: null }, { draw: false });
  await refresh();
  const d = store.data;
  if (!d || d.isSample) return;
  const as = (id: string) => ({ dataset: { id } }) as unknown as HTMLElement;
  if (link.kind === 'splits') {
    if (settleVisible(d.partner) && !ui.settle) actions['settle-screen']({ dataset: {} } as unknown as HTMLElement);
  } else if (link.kind === 'accept') {
    if (d.splitRequests.some((r) => r.id === link.id)) actions['split-accept-open'](as(link.id));
    else toast(LINK_SPLIT_GONE);
  } else if (link.kind === 'tx') {
    const t = d.transactions.find((x) => x.id === link.id);
    if (t) actions[sheetFor(t)](as(t.id));
    else go({ to: 'activity', filter: 'new' });
  }
}

// A link opened while the app is already showing in this tab only changes the hash.
window.addEventListener('hashchange', () => void handleLinkInUrl());

// A tapped notification while the app is open: the service worker asks this window to go there.
navigator.serviceWorker?.addEventListener('message', (e: MessageEvent) => {
  const url = (e.data as { type?: string; url?: string } | null)?.type === 'open' ? (e.data as { url: string }).url : null;
  if (!url) return;
  // Every notification link is a hash on '/': setting it runs handleLinkInUrl() (hashchange).
  if (url.startsWith('/#') && readPushLink(url.slice(1))) location.hash = url.slice(2);
  else void refresh();
});

void adoptStoredToken()
  .then(() => api.config())
  .then((c) => (publicFeatures = c.features))
  .catch(() => {})
  .then(() => handleLinkInUrl())
  .then((handled) => {
    if (handled) return;
    // Signed in or not is the cookie's business: a 401 shows the welcome screen.
    void refresh();
  });

// Pull to refresh on the four tabs (web/src/pull.ts): never on a detail screen, a sheet or the
// drawer, or while dragging. It only reloads.
const PULL_TABS = new Set(['overview', 'activity', 'insights', 'budget']);
initPull(
  () => !!store.data && !recoveryNext && PULL_TABS.has(ui.tab) && !ui.categoryId && !ui.settle && !ui.cardFlow && !ui.sheet && !ui.drawer && !$('sheet').classList.contains('on'),
  () => refresh(),
);

// Silent background refresh (web/src/pull.ts, BACKGROUND): every 45 seconds while the app is
// visible, never with a sheet, the drawer, a drag or a field in use (a redraw would take it away),
// and not when the last load was under 30 seconds ago. No spinner. Paused while hidden; back in
// the foreground it checks at once (so auto-filed purchases show up), then every 45 seconds.
const busyNow = () =>
  loading ||
  !!recoveryNext ||
  !!ui.sheet ||
  !!ui.drawer ||
  $('sheet').classList.contains('on') ||
  document.documentElement.classList.contains('is-dragging') ||
  (document.activeElement instanceof HTMLElement && !!document.activeElement.closest('input, textarea, select, [contenteditable]'));
let backgroundTimer: ReturnType<typeof setInterval> | undefined;
function backgroundTick() {
  if (!store.data) return;
  if (shouldBackgroundRefresh({ visible: document.visibilityState === 'visible', busy: busyNow(), lastLoaded, now: Date.now() })) void refresh({ silent: true });
}
function backgroundRefresh(on: boolean) {
  clearInterval(backgroundTimer);
  backgroundTimer = on ? setInterval(backgroundTick, BACKGROUND.INTERVAL_MS) : undefined;
}
backgroundRefresh(document.visibilityState === 'visible');
document.addEventListener('visibilitychange', () => {
  const visible = document.visibilityState === 'visible';
  backgroundRefresh(visible);
  if (visible) backgroundTick();
});

// Budget tab drag and drop: saved through the move routes, then the month reloads.
initDrag({
  category: (id, groupId, index) => void mutate(() => api.moveCategory(id, { groupId, index })),
  group: (id, index) => void mutate(() => api.moveGroup(id, { index })),
  refused: () => toast(CROSS_KIND),
});
